import {
  enrichQuestionFromLibrary,
  getQuestionById,
} from "./questions/questionStorage";
import { clientPortalUrl, client_url } from "src/config/config";

/** Public join lives on bms-web (`/join/:slug`), not member/coach portals. */
export const getPublicJoinOrigin = () => {
  const configured = String(client_url || clientPortalUrl || "")
    .trim()
    .replace(/\/+$/, "");
  if (configured) return configured;
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "";
};

export const getPublicJoinBaseUrl = () => {
  const origin = getPublicJoinOrigin();
  return origin ? `${origin}/join/` : "/join/";
};

/** Primary kind: Standard vs Journey */
export const TRACKER_KINDS = {
  STANDARD: "standard",
  JOURNEY: "journey",
};

/** Use-case types under each kind */
export const TRACKER_CATEGORIES = {
  LEAD_CAPTURE: "lead_capture",
  EVENT_REGISTRATION: "event_registration",
  CHALLENGE: "challenge",
  CONSULTATION: "consultation",
  LEADERBOARD: "leaderboard",
  JOURNEY: "journey",
  PROGRAMME_ONBOARDING: "programme_onboarding",
  COACH_REVIEW: "coach_review",
  QUARTERLY_REVIEW: "quarterly_review",
};

export const TRACKER_KIND_OPTIONS = [
  {
    value: TRACKER_KINDS.STANDARD,
    title: "Standard journey",
    description: "One-off capture — lead capture and event registrations.",
  },
  {
    value: TRACKER_KINDS.JOURNEY,
    title: "Journey",
    description:
      "Ongoing coaching over time. Clients see this as a Journey, not a Tracker.",
  },
];

export const TRACKER_CATEGORY_OPTIONS = [
  {
    value: TRACKER_CATEGORIES.LEAD_CAPTURE,
    title: "Lead Capture",
    description: "Campaigns, ads and QR sign-ups for new leads.",
    kinds: [TRACKER_KINDS.STANDARD],
    defaultSubmissionMode: "update_existing",
    comingSoon: false,
  },
  {
    value: TRACKER_CATEGORIES.EVENT_REGISTRATION,
    title: "Event Registration",
    description: "Register people for events and mark attendance.",
    kinds: [TRACKER_KINDS.STANDARD],
    defaultSubmissionMode: "update_existing",
    comingSoon: true,
  },
  {
    value: TRACKER_CATEGORIES.CHALLENGE,
    title: "Challenge",
    description: "Challenge journeys with entries, uploads and progress.",
    kinds: [TRACKER_KINDS.JOURNEY],
    defaultSubmissionMode: "update_existing",
    comingSoon: true,
  },
  {
    value: TRACKER_CATEGORIES.JOURNEY,
    title: "Journey",
    description: "Ongoing transformation or coaching journey with reviews.",
    kinds: [TRACKER_KINDS.JOURNEY],
    defaultSubmissionMode: "update_existing",
    comingSoon: false,
  },
  {
    value: TRACKER_CATEGORIES.PROGRAMME_ONBOARDING,
    title: "Programme Onboarding",
    description: "Coach-led onboarding after a programme purchase.",
    kinds: [TRACKER_KINDS.JOURNEY],
    defaultSubmissionMode: "update_existing",
    comingSoon: true,
  },
  {
    value: TRACKER_CATEGORIES.CONSULTATION,
    title: "Consultation",
    description: "Consultation requests and booking follow-up.",
    kinds: [TRACKER_KINDS.JOURNEY],
    defaultSubmissionMode: "update_existing",
    comingSoon: true,
  },
  {
    value: TRACKER_CATEGORIES.COACH_REVIEW,
    title: "Coach Review",
    description: "Recurring coach review checkpoints.",
    kinds: [TRACKER_KINDS.JOURNEY],
    defaultSubmissionMode: "update_existing",
    comingSoon: true,
  },
  {
    value: TRACKER_CATEGORIES.QUARTERLY_REVIEW,
    title: "Quarterly Member Review",
    description: "Fixed-cycle quarterly member reviews.",
    kinds: [TRACKER_KINDS.JOURNEY],
    defaultSubmissionMode: "update_existing",
    comingSoon: true,
  },
  {
    value: TRACKER_CATEGORIES.LEADERBOARD,
    title: "Leaderboard",
    description: "Scores, times and GymTV rankings.",
    kinds: [TRACKER_KINDS.STANDARD],
    defaultSubmissionMode: "update_existing",
    comingSoon: true,
  },
];

/** Create-step cards — plain wording from the GymRite idea build. */
export const CAPTURE_INTENT_OPTIONS = [
  {
    id: "journey",
    category: TRACKER_CATEGORIES.JOURNEY,
    kind: TRACKER_KINDS.JOURNEY,
    title: "An ongoing journey with check-ins and reviews",
    description:
      "A Start form, check-ins on set days, reviews at set weeks, the coach's review form.",
    available: true,
  },
  {
    id: "lead",
    category: TRACKER_CATEGORIES.LEAD_CAPTURE,
    kind: TRACKER_KINDS.STANDARD,
    title: "Lead Capture Form",
    description:
      "A simple personal-info form, optional extra questions, and who follows up.",
    available: false,
  },
  {
    id: "event",
    category: TRACKER_CATEGORIES.EVENT_REGISTRATION,
    kind: TRACKER_KINDS.STANDARD,
    title: "Sign-ups for an event",
    description: "Names and places, teams if you like, a register on the day.",
    available: false,
  },
  {
    id: "challenge",
    category: TRACKER_CATEGORIES.CHALLENGE,
    kind: TRACKER_KINDS.JOURNEY,
    title: "Results for a challenge",
    description:
      "One number each, a video from the phone, coach-verified, on the leaderboard.",
    available: false,
  },
  {
    id: "consultation",
    category: TRACKER_CATEGORIES.CONSULTATION,
    kind: TRACKER_KINDS.JOURNEY,
    title: "Requests for a free consultation",
    description: "Book a chat; the coach records the outcome.",
    available: false,
  },


];

export const JOURNEY_DURATION_OPTIONS = [
  { value: "ongoing", label: "Ongoing — no end" },
  { value: 4, label: "4 weeks" },
  { value: 6, label: "6 weeks" },
  { value: 8, label: "8 weeks" },
  { value: 10, label: "10 weeks" },
  { value: 12, label: "12 weeks" },
  { value: 13, label: "13 weeks" },
  { value: 16, label: "16 weeks" },
  { value: 20, label: "20 weeks" },
  { value: 24, label: "24 weeks" },
  { value: 26, label: "26 weeks" },
  { value: 39, label: "39 weeks" },
  { value: 52, label: "52 weeks" },
  { value: "custom", label: "Custom days" },
];

export const MEMBER_CHECKIN_MODE_OPTIONS = [
  { value: "daily", title: "Daily" },
  { value: "weekly", title: "Weekly" },
  { value: "monthly", title: "Monthly" },
  { value: "custom", title: "Custom" },
];

export const WEEKDAY_OPTIONS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

export const MONTHLY_OPTIONS = [
  { value: "on_this_date", title: "On this date" },
  { value: "on_the_week_and_day", title: "On the week and day" },
];

export const WEEK_AND_DAY_OPTIONS = [
  { value: "first", label: "First" },
  { value: "second", label: "Second" },
  { value: "third", label: "Third" },
  { value: "fourth", label: "Fourth" },
  { value: "last", label: "Last" },
];


export const WEEK_DAY_OPTIONS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];


export const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}));

export const COACH_REVIEW_CADENCE_OPTIONS = [
  {
    value: "every_n_weeks",
    title: "Set weeks (per person)",
    description:
      "Not every check-in. Each person gets coach-review points at week 4, 8, 12… from their journey start (or publish date). Same rule for everyone; timing follows each person's clock.",
  },
  {
    value: "each_checkin",
    title: "Each check-in",
    description:
      "The coach reviews every check-in that person submits — one review per submission.",
  },
  {
    value: "quarterly",
    title: "Fixed dates (whole tracker)",
    description:
      "Everyone on this tracker is reviewed on the same calendar dates (e.g. 1 Jan, 1 Apr, 1 Jul, 1 Oct), no matter when they joined.",
  },
];

/** Scheduled reviews segmented control — GymRite Reviews section. */
export const SCHEDULED_REVIEWS_OPTIONS = [
  { value: "none", label: "None" },
  { value: "by_week", label: "By week" },
  { value: "by_day", label: "By day" },
  { value: "fixed_dates", label: "Fixed dates" },
];

export const REPLY_WITHIN_DAYS_OPTIONS = [
  { value: 0, label: "the same day" },
  { value: 1, label: "1 day" },
  { value: 2, label: "2 days" },
  { value: 3, label: "3 days" },
  { value: 5, label: "5 days" },
  { value: 7, label: "7 days" },
];

export const DEFAULT_DAY_REVIEW_CHIPS = [7, 14, 30, 60, 90];

export const DEFAULT_QUARTERLY_FIXED_DATES = () => [
  { id: "cal_q1", label: "Q1 Review", month: 1, day: 1 },
  { id: "cal_q2", label: "Q2 Review", month: 4, day: 1 },
  { id: "cal_q3", label: "Q3 Review", month: 7, day: 1 },
  { id: "cal_q4", label: "Q4 Review", month: 10, day: 1 },
];

export const COACH_REVIEW_EVERY_WEEKS_OPTIONS = [2, 3, 4, 6, 8, 12];

export const isJourneyDurationUnlimited = (setup = {}) =>
  setup.durationWeeks === "ongoing" || setup.durationWeeks == null;

/** Journey length in whole weeks (for week chips). Ongoing → 12 (UI default only). */
export const getJourneyDurationWeeks = (setup = {}) => {
  const duration = setup.durationWeeks;
  if (duration === "ongoing" || duration == null) return 12;
  if (duration === "custom") {
    const days = Math.max(1, Number(setup.customDurationDays) || 21);
    return Math.max(1, Math.floor(days / 7) || 1);
  }
  return Math.max(1, Number(duration) || 12);
};

/** Journey length in days (null = ongoing / unlimited). */
export const getJourneyDurationDays = (setup = {}) => {
  const duration = setup.durationWeeks;
  if (duration === "ongoing" || duration == null) return null;
  if (duration === "custom") {
    return Math.max(1, Number(setup.customDurationDays) || 21);
  }
  return Math.max(1, Number(duration) || 12) * 7;
};

/**
 * Hard max review week from How long. null = unlimited (ongoing).
 * Custom days → floor(days/7); fixed weeks → that week count.
 */
export const getMaxReviewWeek = (setup = {}) => {
  if (isJourneyDurationUnlimited(setup)) return null;
  return getJourneyDurationWeeks(setup);
};

/**
 * Hard max review day from How long. null = unlimited (ongoing).
 * Custom → custom days; fixed weeks → weeks × 7.
 */
export const getMaxReviewDay = (setup = {}) => {
  if (isJourneyDurationUnlimited(setup)) return null;
  return getJourneyDurationDays(setup);
};

/** @deprecated use getMaxReviewWeek — kept for callers; no grace beyond How long. */
export const getReviewHorizonWeeks = (setup = {}) =>
  getMaxReviewWeek(setup) ?? getJourneyDurationWeeks(setup);

/** @deprecated use getMaxReviewDay — kept for callers; no grace beyond How long. */
export const getReviewHorizonDays = (setup = {}) =>
  getMaxReviewDay(setup) ?? getJourneyDurationWeeks(setup) * 7;

/** Week chips to show (1…max, or 1…12 when ongoing). */
export const getReviewWeekChipList = (setup = {}, selected = []) => {
  const max = getMaxReviewWeek(setup);
  const base =
    max == null
      ? Array.from({ length: 12 }, (_, i) => i + 1)
      : Array.from({ length: max }, (_, i) => i + 1);
  const extras = (Array.isArray(selected) ? selected : [])
    .map((w) => Number(w))
    .filter((w) => Number.isFinite(w) && w >= 1 && (max == null || w <= max));
  return Array.from(new Set([...base, ...extras])).sort((a, b) => a - b);
};

/** Day chips within How long (presets + selected, capped). */
export const getReviewDayChipList = (setup = {}, selected = []) => {
  const max = getMaxReviewDay(setup);
  const within = (d) => Number.isFinite(d) && d >= 1 && (max == null || d <= max);
  let presets = DEFAULT_DAY_REVIEW_CHIPS.filter(within);
  if (max != null && max > 0) {
    const stepped = [];
    for (let d = 7; d <= max; d += 7) stepped.push(d);
    if (!stepped.includes(max) && max >= 1) stepped.push(max);
    presets = Array.from(new Set([...presets, ...stepped])).filter(within);
  }
  const extras = (Array.isArray(selected) ? selected : [])
    .map((d) => Number(d))
    .filter(within);
  return Array.from(new Set([...presets, ...extras])).sort((a, b) => a - b);
};

/** Migrate legacy coachReviewMode → scheduledReviews. */
export const normalizeScheduledReviews = (setup = {}) => {
  const raw = setup.scheduledReviews;
  if (
    raw === "none" ||
    raw === "by_week" ||
    raw === "by_day" ||
    raw === "fixed_dates"
  ) {
    return raw;
  }
  if (setup.coachReviewMode === "quarterly") return "fixed_dates";
  if (setup.coachReviewMode === "each_checkin") return "none";
  if (setup.coachReviewMode === "by_day") return "by_day";
  if (setup.coachReviewMode === "none") return "none";
  return "by_week";
};

export const normalizeAlsoReviewEveryCheckin = (setup = {}) => {
  const scheduled = normalizeScheduledReviews(setup);
  if (scheduled === "none") return true;
  if (typeof setup.alsoReviewEveryCheckin === "boolean") {
    return setup.alsoReviewEveryCheckin;
  }
  if (setup.coachReviewMode === "each_checkin") return true;
  // Legacy scheduled-only cadences did not reply to every check-in.
  return false;
};

export const normalizeReplyWithinDays = (value) => {
  const n = Number(value);
  if (REPLY_WITHIN_DAYS_OPTIONS.some((item) => item.value === n)) return n;
  return 2;
};

export const normalizeCoachReviewWeeks = (weeks, setup = {}) => {
  const max = getMaxReviewWeek(setup);
  const list = (Array.isArray(weeks) ? weeks : [])
    .map((w) => Number(w))
    .filter(
      (w) =>
        Number.isFinite(w) &&
        w >= 1 &&
        (max == null ? w <= 520 : w <= max)
    );
  const unique = Array.from(new Set(list)).sort((a, b) => a - b);
  if (unique.length) return unique;
  // A list the admin emptied stays empty — no auto-filled weeks.
  if (Array.isArray(weeks) && weeks.length === 0) return [];
  const every = Math.max(1, Number(setup.coachReviewEveryWeeks) || 4);
  const duration = max == null ? Math.max(every * 3, 12) : max;
  const offsets = [];
  for (let week = every; week <= duration; week += every) {
    offsets.push(week);
  }
  if (offsets.length) return offsets;
  return max == null ? [every] : [Math.min(every, max)];
};

export const normalizeCoachReviewDays = (days, setup = {}) => {
  const max = getMaxReviewDay(setup);
  const list = (Array.isArray(days) ? days : [])
    .map((d) => Number(d))
    .filter(
      (d) =>
        Number.isFinite(d) &&
        d >= 1 &&
        (max == null ? d <= 3650 : d <= max)
    );
  const unique = Array.from(new Set(list)).sort((a, b) => a - b);
  if (unique.length) return unique;
  // A list the admin emptied stays empty — no auto-filled days.
  if (Array.isArray(days) && days.length === 0) return [];
  const presets = DEFAULT_DAY_REVIEW_CHIPS.filter(
    (d) => max == null || d <= max
  );
  if (presets.length) return presets;
  if (max == null) return [30, 60, 90];
  return [max];
};

export const normalizeCoachReviewFixedDates = (dates) => {
  // A list the admin emptied stays empty — no auto-filled quarterly dates.
  if (Array.isArray(dates) && dates.length === 0) return [];
  if (Array.isArray(dates) && dates.length) {
    return dates
      .map((item, index) => {
        const month = Math.min(12, Math.max(1, Number(item.month) || 1));
        const day = Math.min(28, Math.max(1, Number(item.day) || 1));
        const monthLabel =
          [
            "",
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ][month] || "Date";
        return {
          id: item.id || `cal_${month}_${day}_${index}`,
          label: item.label || `${monthLabel} ${day}`,
          month,
          day,
        };
      })
      .filter(Boolean);
  }
  return DEFAULT_QUARTERLY_FIXED_DATES();
};

/** Keep legacy coachReviewMode in sync with the new Reviews controls. */
export const deriveCoachReviewMode = (setup = {}) => {
  const scheduled = normalizeScheduledReviews(setup);
  if (scheduled === "fixed_dates") return "quarterly";
  if (scheduled === "by_day") return "by_day";
  if (scheduled === "by_week") return "every_n_weeks";
  return "each_checkin";
};

/** Normalize createSetup coach-review fields (scheduled + also check-in). */
export const normalizeJourneyCoachReviewSetup = (setup = {}) => {
  const defaults = createDefaultJourneyCreateSetup();
  const hasAlsoFlag = Object.prototype.hasOwnProperty.call(
    setup,
    "alsoReviewEveryCheckin"
  );
  const hasScheduledFlag = Object.prototype.hasOwnProperty.call(
    setup,
    "scheduledReviews"
  );
  const base = { ...defaults, ...setup };
  const scheduledReviews = hasScheduledFlag
    ? normalizeScheduledReviews(setup)
    : normalizeScheduledReviews(setup);

  let alsoReviewEveryCheckin;
  if (hasAlsoFlag) {
    alsoReviewEveryCheckin =
      scheduledReviews === "none" ? true : Boolean(setup.alsoReviewEveryCheckin);
  } else if (scheduledReviews === "none") {
    alsoReviewEveryCheckin = true;
  } else if (setup.coachReviewMode === "each_checkin") {
    alsoReviewEveryCheckin = true;
  } else if (
    setup.coachReviewMode === "every_n_weeks" ||
    setup.coachReviewMode === "quarterly" ||
    setup.coachReviewMode === "by_day"
  ) {
    // Legacy scheduled-only cadences.
    alsoReviewEveryCheckin = false;
  } else {
    alsoReviewEveryCheckin = defaults.alsoReviewEveryCheckin;
  }

  const replyWithinDays = normalizeReplyWithinDays(
    setup.replyWithinDays != null
      ? setup.replyWithinDays
      : defaults.replyWithinDays
  );
  const coachReviewWeeks = normalizeCoachReviewWeeks(
    Array.isArray(setup.coachReviewWeeks) ? setup.coachReviewWeeks : null,
    base
  );
  const coachReviewDays = normalizeCoachReviewDays(
    Array.isArray(setup.coachReviewDays) ? setup.coachReviewDays : null,
    base
  );
  const coachReviewFixedDates = normalizeCoachReviewFixedDates(
    Array.isArray(setup.coachReviewFixedDates)
      ? setup.coachReviewFixedDates
      : setup.coachReviewMode === "quarterly"
        ? null
        : base.coachReviewFixedDates
  );
  return {
    ...base,
    checkInMode: normalizeCheckInMode(base.checkInMode),
    checkInMonthDay: normalizeCheckInMonthDay(base.checkInMonthDay),
    checkInMonthlyStyle: normalizeCheckInMonthlyStyle(base.checkInMonthlyStyle),
    checkInMonthWeekOrdinal: normalizeCheckInMonthWeekOrdinal(
      base.checkInMonthWeekOrdinal
    ),
    checkInMonthWeekday: normalizeCheckInMonthWeekday(base.checkInMonthWeekday),
    scheduledReviews,
    alsoReviewEveryCheckin,
    replyWithinDays,
    coachReviewWeeks,
    coachReviewDays,
    coachReviewFixedDates,
    coachReviewEveryWeeks: Math.max(1, Number(base.coachReviewEveryWeeks) || 4),
    coachReviewMode: deriveCoachReviewMode({
      ...base,
      scheduledReviews,
      alsoReviewEveryCheckin,
    }),
  };
};

/** Trigger — derived from also-review-every-check-in (scheduled can combine). */
export const getCoachReviewTriggerForMode = (modeOrSetup) => {
  if (modeOrSetup && typeof modeOrSetup === "object") {
    return normalizeAlsoReviewEveryCheckin(modeOrSetup)
      ? "after_each_submission"
      : "after_follow_ups";
  }
  if (modeOrSetup === "each_checkin" || modeOrSetup === "none") {
    return "after_each_submission";
  }
  return "after_follow_ups";
};

export const isCoachReviewEveryWeeksPreset = (weeks) =>
  COACH_REVIEW_EVERY_WEEKS_OPTIONS.includes(Number(weeks));

/** YYYY-MM-DD for today (local). */
export const getTodayDateString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const createDefaultJourneyCreateSetup = () => ({
  durationWeeks: 12,
  customDurationDays: 21,
  journeyStartsFrom: "from_people_start",
  publishDate: getTodayDateString(),
  checkInMode: "weekly",
  checkInWeekdays: [4],
  checkInMonthDay: 1,
  checkInMonthlyStyle: "on_this_date",
  checkInMonthWeekOrdinal: "first",
  checkInMonthWeekday: "monday",
  checkInEveryDays: 7,
  scheduledReviews: "by_week",
  coachReviewWeeks: [4, 8, 12],
  coachReviewDays: [30, 60, 90],
  coachReviewFixedDates: DEFAULT_QUARTERLY_FIXED_DATES(),
  alsoReviewEveryCheckin: true,
  replyWithinDays: 2,
  coachReviewMode: "every_n_weeks",
  coachReviewEveryWeeks: 4,
});

/** Migrate older check-in modes to calendar-style cadence. */
export const normalizeCheckInMode = (value) => {
  if (value === "daily") return "daily";
  if (value === "weekly" || value === "weekday") return "weekly";
  if (value === "monthly") return "monthly";
  if (value === "custom" || value === "every_n_days") return "custom";
  return "weekly";
};

export const normalizeCheckInMonthDay = (value) => {
  const day = Number(value);
  if (Number.isFinite(day) && day >= 1 && day <= 31) return day;
  return 1;
};

export const normalizeCheckInMonthlyStyle = (value) =>
  value === "on_the_week_and_day" ? "on_the_week_and_day" : "on_this_date";

export const normalizeCheckInMonthWeekOrdinal = (value) => {
  const allowed = WEEK_AND_DAY_OPTIONS.map((item) => item.value);
  return allowed.includes(value) ? value : "first";
};

export const normalizeCheckInMonthWeekday = (value) => {
  const key = String(value || "").toLowerCase();
  const allowed = WEEK_DAY_OPTIONS.map((item) => item.value);
  return allowed.includes(key) ? key : "monday";
};

/** Days between member check-ins for schedule / unlock helpers. */
export const getCheckInIntervalDays = (setup = {}) => {
  const mode = normalizeCheckInMode(setup.checkInMode);
  if (mode === "daily") return 1;
  if (mode === "monthly") return 30;
  if (mode === "custom") {
    return Math.max(1, Number(setup.checkInEveryDays) || 7);
  }
  // weekly — approximate cadence for unlocks
  const days = Array.isArray(setup.checkInWeekdays)
    ? setup.checkInWeekdays.length
    : 0;
  if (days >= 2) return Math.max(1, Math.round(7 / days));
  return 7;
};

export const describeCheckInCadence = (setup) => {
  if (!setup) return "—";
  const mode = normalizeCheckInMode(setup.checkInMode);
  if (mode === "daily") return "Daily";
  if (mode === "monthly") {
    const style = normalizeCheckInMonthlyStyle(setup.checkInMonthlyStyle);
    if (style === "on_this_date") {
      const day = normalizeCheckInMonthDay(setup.checkInMonthDay);
      return `Monthly · day ${day} of each month`;
    }
    const ordLabel =
      WEEK_AND_DAY_OPTIONS.find(
        (item) =>
          item.value ===
          normalizeCheckInMonthWeekOrdinal(setup.checkInMonthWeekOrdinal)
      )?.label || "First";
    const dayLabel =
      WEEK_DAY_OPTIONS.find(
        (item) =>
          item.value === normalizeCheckInMonthWeekday(setup.checkInMonthWeekday)
      )?.label || "Monday";
    return `Monthly · ${ordLabel} ${dayLabel} of each month`;
  }
  if (mode === "weekly") {
    // Mon → Sun, whatever order the days were picked in.
    const weekdayRank = (d) => {
      const index = WEEKDAY_OPTIONS.findIndex((item) => item.value === d);
      return index === -1 ? WEEKDAY_OPTIONS.length : index;
    };
    const days = [...(setup.checkInWeekdays || [])]
      .sort((a, b) => weekdayRank(a) - weekdayRank(b))
      .map((d) => WEEKDAY_OPTIONS.find((item) => item.value === d)?.label || d)
      .join(", ");
    return days ? `Weekly · ${days}` : "Weekly";
  }
  const n = Math.max(1, Number(setup.checkInEveryDays) || 7);
  return `Every ${n} day${n === 1 ? "" : "s"}`;
};

/** Duration default from a gym programme (limited weeks vs unlimited). */
export const resolveDurationFromProgramme = (programme) => {
  if (!programme) return null;
  const lengthType = String(
    programme.program_length_type || programme.lengthType || ""
  ).toLowerCase();
  if (lengthType === "unlimited") return "ongoing";
  const weeks = Number(programme.no_of_weeks ?? programme.weeks);
  if (Number.isFinite(weeks) && weeks > 0) return weeks;
  if (lengthType === "limited") return 12;
  return "ongoing";
};

export const describeDurationWeeks = (durationWeeks, customDurationDays) => {
  if (durationWeeks === "ongoing" || durationWeeks == null) {
    return "Ongoing — no end";
  }
  if (durationWeeks === "custom") {
    const days = Math.max(1, Number(customDurationDays) || 21);
    return `${days} days`;
  }
  if (durationWeeks === "") return "Not set";
  return `${durationWeeks} weeks`;
};

export const addDaysToDateString = (ymd, days) => {
  if (!ymd) return null;
  const parts = String(ymd).slice(0, 10).split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  date.setDate(date.getDate() + Number(days || 0));
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const formatDisplayDate = (ymd) => {
  if (!ymd) return "—";
  const parts = String(ymd).slice(0, 10).split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return "—";
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/** End date from publish + How long (null if ongoing / missing). */
export const getJourneyEndDateFromPublish = (setup = {}) => {
  const publish = setup.publishDate || null;
  if (!publish) return null;
  if (isJourneyDurationUnlimited(setup)) return null;
  if (setup.durationWeeks === "custom") {
    const days = Math.max(1, Number(setup.customDurationDays) || 21);
    return addDaysToDateString(publish, days);
  }
  const weeks = Math.max(1, Number(setup.durationWeeks) || 12);
  return addDaysToDateString(publish, weeks * 7);
};

/** Clear window label when clock starts from publish date. */
export const describeJourneyWindowFromPublish = (setup = {}) => {
  const publish = setup.publishDate || null;
  if (!publish) return "Set a publish date to see the journey window.";
  const startLabel = formatDisplayDate(publish);
  if (isJourneyDurationUnlimited(setup)) {
    return `${startLabel} → ongoing`;
  }
  const end = getJourneyEndDateFromPublish(setup);
  const duration = describeDurationWeeks(
    setup.durationWeeks,
    setup.customDurationDays
  );
  return `${startLabel} → ${formatDisplayDate(end)} (${duration})`;
};

/**
 * Calendar date for a schedule point when the journey clock starts on publish.
 * Day 1 = publish day; Week N = publish + N weeks; Month N = publish + N months.
 */
export const getReviewPointDateFromPublish = (publishDate, point = {}) => {
  if (!publishDate) return null;
  const amount = Math.max(1, Number(point.amount) || 1);
  const unit = point.unit || "weeks";
  if (unit === "days") {
    return addDaysToDateString(publishDate, amount - 1);
  }
  if (unit === "months") {
    const parts = String(publishDate).slice(0, 10).split("-").map(Number);
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
      return null;
    }
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    date.setMonth(date.getMonth() + amount);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return addDaysToDateString(publishDate, amount * 7);
};

/** Labeled meta rows under the tracker name in the list. */
export const getTrackerListMetaParts = (tracker) => {
  if (!tracker) return [];
  const parts = [];
  const setup = tracker.createSetup;

  parts.push({
    label: "Access",
    value: getTrackerAccessLabel(tracker.trackerAccess, tracker),
  });

  if (setup) {
    const durationWeeks = setup.durationWeeks;
    let durationValue = "Ongoing";
    if (durationWeeks === "custom") {
      const days = Math.max(1, Number(setup.customDurationDays) || 21);
      durationValue = `${days} days`;
    } else if (durationWeeks === "") {
      durationValue = "Not set";
    } else if (durationWeeks !== "ongoing" && durationWeeks != null) {
      const weeks = Number(durationWeeks);
      durationValue = weeks === 1 ? "1 week" : `${weeks} weeks`;
    }
    parts.push({ label: "Length", value: durationValue });

    const checkIn = describeCheckInCadence(setup);
    if (checkIn && checkIn !== "—") {
      parts.push({ label: "Check-in", value: checkIn });
    }
  } else {
    const kind = getKindLabel(tracker.kind);
    const category = getCategoryLabel(tracker.category);
    // One "Type: Standard journey · Lead Capture" part.
    const typeValue = [kind, category !== kind ? category : ""]
      .filter((item) => item && item !== "—")
      .join(" · ");
    if (typeValue) parts.push({ label: "Type", value: typeValue });
  }

  const formCount = Array.isArray(tracker.forms) ? tracker.forms.length : 0;
  if (formCount > 0) {
    parts.push({
      label: "Forms",
      value: `${formCount}`,
    });
  }

  const checkpointCount = countSubmissionCheckpoints(
    tracker.submissionSchedule
  );
  if (checkpointCount > 0) {
    parts.push({
      label: "Checkpoints",
      value: `${checkpointCount}`,
    });
  }

  const coachCount = Array.isArray(tracker.coaches)
    ? tracker.coaches.length
    : 0;
  if (coachCount > 0) {
    parts.push({
      label: "Coaches",
      value: `${coachCount}`,
    });
  }

  return parts;
};

/** @deprecated use getTrackerListMetaParts */
export const getTrackerListMetaLine = (tracker) =>
  getTrackerListMetaParts(tracker)
    .map((item) =>
      typeof item === "string" ? item : `${item.label}: ${item.value}`
    )
    .join(" · ");

/** Programme-linked / GymRite-only trackers are not offered on a public link. */
export const TRACKER_ACCESS = {
  PUBLIC: "public",
  GYMRITE_MEMBERS: "gymrite_members",
};

export const TRACKER_ACCESS_OPTIONS = [
  { value: TRACKER_ACCESS.PUBLIC, label: "Public" },
  {
    value: TRACKER_ACCESS.GYMRITE_MEMBERS,
    label: "GymRite members (selected programmes)",
  },
];

export const trackerHasProgramme = (tracker) =>
  Array.isArray(tracker?.programmes) && tracker.programmes.length > 0;

/** Resolve access; migrate older trackers from programme linkage. */
export const normalizeTrackerAccess = (value, tracker) => {
  if (
    value === TRACKER_ACCESS.PUBLIC ||
    value === TRACKER_ACCESS.GYMRITE_MEMBERS
  ) {
    return value;
  }
  // Backend alias for programme-only access.
  if (value === "programme_members") return TRACKER_ACCESS.GYMRITE_MEMBERS;
  return trackerHasProgramme(tracker)
    ? TRACKER_ACCESS.GYMRITE_MEMBERS
    : TRACKER_ACCESS.PUBLIC;
};

/** Public tracker that asks for a password before the join form. */
export const isTrackerPasswordProtected = (tracker) =>
  normalizeTrackerAccess(tracker?.trackerAccess, tracker) ===
    TRACKER_ACCESS.PUBLIC && Boolean(tracker?.settings?.requirePassword);

// withPassword: false where a separate "Password protected" fact is shown.
export const getTrackerAccessLabel = (value, tracker, { withPassword = true } = {}) => {
  const access = normalizeTrackerAccess(value, tracker);
  if (access === TRACKER_ACCESS.GYMRITE_MEMBERS) return "GymRite members";
  return withPassword && tracker?.settings?.requirePassword
    ? "Public · Password protected"
    : "Public";
};

export const isTrackerPublicLinkAvailable = (tracker) =>
  normalizeTrackerAccess(tracker?.trackerAccess, tracker) ===
  TRACKER_ACCESS.PUBLIC;

/** Public join URL for a tracker (empty when not public). Hosted on client portal. */
export const getTrackerPublicUrl = (tracker) => {
  if (!isTrackerPublicLinkAvailable(tracker)) return "";
  const slug = tracker?.publicSlug || tracker?.id;
  if (!slug) return "";
  const origin = getPublicJoinOrigin();
  return origin ? `${origin}/join/${slug}` : `/join/${slug}`;
};

export const withProgrammePublicRules = (tracker) => {
  if (!tracker) return tracker;
  const access = normalizeTrackerAccess(tracker.trackerAccess, tracker);
  if (access === TRACKER_ACCESS.PUBLIC) {
    return {
      ...tracker,
      trackerAccess: TRACKER_ACCESS.PUBLIC,
      programmes: [],
    };
  }
  return {
    ...tracker,
    trackerAccess: TRACKER_ACCESS.GYMRITE_MEMBERS,
  };
};

export const OPEN_SCHEDULE_OPTIONS = [
  { value: "always_open", label: "Always open" },
  { value: "opens_on_date", label: "Opens on a date" },
];

/** Journey length is How long — drop redundant close/window schedules. */
export const normalizeOpenSchedule = (value) => {
  if (value === "opens_on_date") return "opens_on_date";
  return "always_open";
};

/** When the journey clock starts for relative schedules. */
export const JOURNEY_START_OPTIONS = [
  {
    value: "from_people_start",
    label: "From Person's Submission date",
    description: "",
  },
  {
    value: "from_publish_date",
    label: "From publish date",
    description: "",
  },
];

/** Migrate older journey-clock values to the two UI options. */
export const normalizeJourneyStartsFrom = (value) => {
  if (
    value === "initial_submission" ||
    value === "programme_start" ||
    value === "from_people_start"
  ) {
    return "from_people_start";
  }
  if (value === "coach_defined" || value === "from_publish_date") {
    return "from_publish_date";
  }
  return "from_people_start";
};

export const SUBMISSION_MODES = {
  NEW_ENTRY: "new_entry",
  UPDATE_EXISTING: "update_existing",
};

/** When Update Existing finds no matching Person (email → phone). */
export const NO_MATCH_BEHAVIOURS = {
  CREATE: "create",
  ERROR: "error",
  ADMIN_REVIEW: "admin_review",
};

export const NO_MATCH_BEHAVIOUR_OPTIONS = [
  {
    value: NO_MATCH_BEHAVIOURS.CREATE,
    title: "Create a new person",
    description: "If email and phone do not match anyone, start a new record.",
  },
  {
    value: NO_MATCH_BEHAVIOURS.ERROR,
    title: "Show an error",
    description: "Block the submission and ask them to check their details.",
  },
  {
    value: NO_MATCH_BEHAVIOURS.ADMIN_REVIEW,
    title: "Send to admin review",
    description: "Hold the submission until a coach or admin resolves it.",
  },
];

export const AUTOMATION_RECIPIENTS = [
  { value: "person", label: "Person only" },
  { value: "coach", label: "Assigned coach only" },
  { value: "both", label: "Person and coach" },
];

/**
 * Events Trackers expose for automation (GymRite doc list).
 * Engines subscribe — not hard-wired to Power Automate.
 */
export const AUTOMATION_EVENT_OPTIONS = [
  {
    id: "new_submission",
    label: "New Submission",
    description: "A person submits a form on this journey for the first time or a new entry.",
  },
  {
    id: "submission_updated",
    label: "Submission Updated",
    description: "An existing journey entry is updated (Update Existing).",
  },
  {
    id: "review_due",
    label: "Review Due",
    description: "A scheduled review / submission checkpoint becomes due.",
  },
  {
    id: "review_overdue",
    label: "Review Overdue",
    description: "A due review has passed without completion.",
  },
  {
    id: "follow_up_outstanding",
    label: "Follow-up Outstanding",
    description: "A follow-up action is waiting on the person or coach.",
  },
  {
    id: "follow_up_completed",
    label: "Follow-up Completed",
    description: "A follow-up workflow has been completed.",
  },
  {
    id: "status_changed",
    label: "Status Changed",
    description: "A coach or admin moves the person to another status.",
  },
  {
    id: "member_left",
    label: "Member left journey",
    description: "Fires when a member leaves the journey from their portal.",
  },
  {
    id: "milestone_achieved",
    label: "Milestone Achieved",
    description: "A measurable or journey milestone is reached.",
  },
  {
    id: "file_uploaded",
    label: "File Uploaded",
    description: "A file is uploaded on an entry.",
  },
  {
    id: "image_uploaded",
    label: "Image Uploaded",
    description: "An image is uploaded on an entry.",
  },
];

/**
 * Coach capabilities from Collaboration & Permissions (doc).
 * Toggleable by admin. Locked cannots are shown separately in UI.
 */
export const COACH_PERMISSION_OPTIONS = [
  {
    key: "coachesCanViewAssignedEntries",
    label: "View their own assigned Tracker Entries",
    help: "Coaches only see entries assigned to them.",
    default: true,
  },
  {
    key: "coachesCanSearchPeople",
    label: "Search for people",
    help: "Find Person records linked to this journey.",
    default: true,
  },
  {
    key: "coachesCanCreateEntries",
    label: "Create Tracker Entries",
    help: "Add entries on behalf of a person when needed.",
    default: true,
  },
  {
    key: "coachesCanUpdateEntries",
    label: "Update Tracker Entries",
    help: "Edit answers and move people through statuses.",
    default: true,
  },
  {
    key: "coachesCanCompleteReviews",
    label: "Complete reviews",
    help: "Answer coach review questions and clear Review Due / Review Overdue.",
    default: true,
  },
  {
    key: "coachesCanUpload",
    label: "Upload files and images",
    help: "Image Upload and File Upload are mandatory tracker features.",
    default: true,
  },
  {
    key: "coachesCanTriggerFollowUps",
    label: "Trigger follow-up workflows",
    help: "Start or nudge follow-ups from an entry.",
    default: true,
  },
  {
    key: "coachesCanChooseTriggerAutomation",
    label: "Choose Trigger associated automation (Yes / No)",
    help: "When submitting on behalf of someone — avoids accidental communications on historical corrections.",
    default: true,
  },
];

/** Per-coach permission mode: all toggles on, or pick specific ones. */
export const COACH_PERMISSION_MODES = {
  ALL: "all",
  SPECIFIC: "specific",
};

export const COACH_PERMISSION_MODE_OPTIONS = [
  { value: COACH_PERMISSION_MODES.ALL, label: "All permissions" },
  { value: COACH_PERMISSION_MODES.SPECIFIC, label: "Specific" },
];

export const createDefaultCoachPermissionFlags = () => {
  const flags = {};
  COACH_PERMISSION_OPTIONS.forEach((item) => {
    flags[item.key] = item.default;
  });
  return flags;
};

export const createAllCoachPermissionFlags = () => {
  const flags = {};
  COACH_PERMISSION_OPTIONS.forEach((item) => {
    flags[item.key] = true;
  });
  return flags;
};

export const normalizeCoachPermissionMode = (value) =>
  value === COACH_PERMISSION_MODES.SPECIFIC
    ? COACH_PERMISSION_MODES.SPECIFIC
    : COACH_PERMISSION_MODES.ALL;

/** Normalize a coach ref including optional per-coach permissions. */
export const normalizeCoachWithPermissions = (item, trackerPermissions = {}) => {
  if (!item) return null;
  const base =
    typeof item === "string"
      ? { id: item, first_name: item, last_name: "", email: "" }
      : {
          id: item.id || item._id,
          first_name: item.first_name || "",
          last_name: item.last_name || "",
          email: item.email || "",
        };
  if (!base.id) return null;

  const mode = normalizeCoachPermissionMode(item.permissionMode);
  const defaults = {
    ...createDefaultCoachPermissionFlags(),
    ...Object.fromEntries(
      COACH_PERMISSION_OPTIONS.map((opt) => [
        opt.key,
        trackerPermissions?.[opt.key] != null
          ? Boolean(trackerPermissions[opt.key])
          : opt.default,
      ])
    ),
  };
  const incoming =
    item.permissions && typeof item.permissions === "object"
      ? item.permissions
      : {};
  const permissions = {};
  COACH_PERMISSION_OPTIONS.forEach((opt) => {
    if (mode === COACH_PERMISSION_MODES.ALL) {
      permissions[opt.key] = true;
    } else if (incoming[opt.key] != null) {
      permissions[opt.key] = Boolean(incoming[opt.key]);
    } else {
      permissions[opt.key] = Boolean(defaults[opt.key]);
    }
  });

  return {
    ...base,
    permissionMode: mode,
    permissions,
  };
};

/** Always false for coaches — administrators only (doc). */
export const COACH_LOCKED_RESTRICTIONS = [
  "Assign other coaches",
  "Modify Tracker structure",
  "Configure GymTV",
  "Modify automation settings",
];

export const createDefaultPermissions = () => {
  const permissions = {
    coachesCanAssignCoaches: false,
    coachesCanEditStructure: false,
    coachesCanEditGymTv: false,
    coachesCanEditAutomation: false,
  };
  COACH_PERMISSION_OPTIONS.forEach((item) => {
    permissions[item.key] = item.default;
  });
  // Legacy keys kept for older saved permission shapes
  permissions.coachesCanEditEntries =
    permissions.coachesCanCreateEntries && permissions.coachesCanUpdateEntries;
  permissions.coachesCanSubmitOnBehalf = permissions.coachesCanCreateEntries;
  permissions.coachesCanTriggerAutomation =
    permissions.coachesCanChooseTriggerAutomation;
  return permissions;
};

export const normalizePermissions = (permissions = {}) => {
  const defaults = createDefaultPermissions();
  const next = { ...defaults, ...permissions };
  // Locked cannots — always false
  next.coachesCanAssignCoaches = false;
  next.coachesCanEditStructure = false;
  next.coachesCanEditGymTv = false;
  next.coachesCanEditAutomation = false;

  // Migrate older keys → doc keys
  if (
    permissions.coachesCanEditEntries != null &&
    permissions.coachesCanCreateEntries == null
  ) {
    next.coachesCanCreateEntries = Boolean(permissions.coachesCanEditEntries);
    next.coachesCanUpdateEntries = Boolean(permissions.coachesCanEditEntries);
  }
  if (
    permissions.coachesCanSubmitOnBehalf != null &&
    permissions.coachesCanCreateEntries == null
  ) {
    next.coachesCanCreateEntries = Boolean(permissions.coachesCanSubmitOnBehalf);
  }
  if (
    permissions.coachesCanTriggerAutomation != null &&
    permissions.coachesCanChooseTriggerAutomation == null
  ) {
    next.coachesCanChooseTriggerAutomation = Boolean(
      permissions.coachesCanTriggerAutomation
    );
  }
  if (permissions.coachesCanCompleteReviews == null) {
    next.coachesCanCompleteReviews = true;
  }
  if (permissions.coachesCanSearchPeople == null) {
    next.coachesCanSearchPeople = true;
  }
  if (permissions.coachesCanViewAssignedEntries == null) {
    next.coachesCanViewAssignedEntries = true;
  }

  return next;
};

export const createDefaultAutomation = () => ({
  /** Parent Automation id — one Automation per Tracker. */
  automationId: "",
  enabled: false,
  recipients: "both",
  notifyUnassignedCoach: true,
  /** Active workflow ids mirrored from the parent Automation. */
  workflowIds: [],
  events: AUTOMATION_EVENT_OPTIONS.map((item) => ({
    id: item.id,
    enabled: [
      "new_submission",
      "review_due",
      "review_overdue",
      "follow_up_outstanding",
    ].includes(item.id),
  })),
});

export const createDefaultGymTvViews = () => [
  {
    id: "view_summary",
    type: "summary",
    name: "Summary",
    enabled: true,
  },
  {
    id: "view_leaderboard",
    type: "leaderboard",
    name: "Leaderboard",
    enabled: true,
    rankingFieldId: "",
    aggregation: "latest",
    direction: "descending",
    topN: 10,
    showTies: true,
  },
];

export const TRACKER_STATUS = {
  DRAFT: "draft",
  LIVE: "live",
  ARCHIVED: "archived",
};

/** When coach review work is created for a member submission. */
export const COACH_REVIEW_TRIGGERS = [
  {
    value: "after_each_submission",
    title: "After every member submission",
    description:
      "Each submission can flag Review Due for the assigned coach.",
  },
  {
    value: "after_follow_ups",
    title: "After follow-up / scheduled forms only",
    description:
      "Sign-up is skipped. Coach review starts from scheduled checkpoints (e.g. 30 / 60 / 90 day).",
  },
  {
    value: "manual",
    title: "Manual only",
    description:
      "Coaches open a review when they choose — nothing is auto-queued from submits.",
  },
];

export const COACH_REVIEW_FIELD_TYPES = [
  { value: "short_text", label: "Short text" },
  { value: "long_text", label: "Long text / notes" },
  { value: "yes_no", label: "Yes / No" },
  { value: "dropdown", label: "Dropdown" },
  { value: "scale", label: "Scale (1–10)" },
  { value: "number", label: "Number / rating" },
];

export const createCoachReviewQuestion = (data = {}, order = 0) => ({
  id: data.id || `crq_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  source: data.source || (data.questionId ? "library" : "tracker"),
  questionId: data.questionId || null,
  label: data.label || "",
  placeholder: data.placeholder || "",
  helpText: data.helpText || "",
  fieldType: data.fieldType || "long_text",
  required: data.required !== false,
  scaleMin: data.scaleMin ?? 1,
  scaleMax: data.scaleMax ?? 10,
  options: Array.isArray(data.options) ? data.options : [],
  order,
});

/** Link a Questions-module Coach review item onto this tracker. */
export const createCoachReviewQuestionFromLibrary = (
  libraryQuestion,
  order = 0
) => {
  const enriched = enrichQuestionFromLibrary(
    {
      source: "library",
      questionId: libraryQuestion.id,
      label: "",
      placeholder: "",
      helpText: "",
      fieldType: "",
      scaleMin: undefined,
      scaleMax: undefined,
      options: [],
      required: true,
    },
    libraryQuestion
  );
  return createCoachReviewQuestion(
    {
      ...enriched,
      source: "library",
      questionId: libraryQuestion.id,
      required: true,
    },
    order
  );
};

export const createDefaultCoachReviewQuestions = () => [
  createCoachReviewQuestion(
    {
      source: "library",
      questionId: "q_cr_coach_notes",
      label: "Coach notes",
      fieldType: "long_text",
      required: false,
      helpText: "Notes from completing this review (visible to coaches/admins).",
    },
    0
  ),
];

/**
 * Coach review / follow-up after members submit (Journey).
 * Entry flags from GymRite Review Due Logic:
 * Review Due · Review Overdue · Awaiting Member Submission
 */
export const createDefaultCoachReview = () => ({
  enabled: true,
  /** After member submit, coach must complete a review. */
  trigger: "after_each_submission",
  dueAfterDays: 0,
  overdueAfterDays: 3,
  requireAllQuestions: false,
  /** Doc Q22 — coaches may override next review date; change is logged. */
  allowOverrideNextReview: true,
  /** Record Submitted By: Member | Coach | Admin | System/Integration */
  recordSubmittedBy: true,
  questions: createDefaultCoachReviewQuestions(),
});

export const normalizeCoachReview = (coachReview) => {
  const defaults = createDefaultCoachReview();
  if (!coachReview || typeof coachReview !== "object") return defaults;

  const questions = Array.isArray(coachReview.questions)
    ? coachReview.questions
        .map((q, index) =>
          createCoachReviewQuestion(
            {
              ...q,
              id: q.id || undefined,
            },
            q.order ?? index
          )
        )
        .filter(Boolean)
    : defaults.questions;

  return {
    ...defaults,
    ...coachReview,
    dueAfterDays: Number(coachReview.dueAfterDays) || 0,
    overdueAfterDays: Math.max(1, Number(coachReview.overdueAfterDays) || 3),
    recordSubmittedBy: coachReview.recordSubmittedBy !== false,
    allowOverrideNextReview: coachReview.allowOverrideNextReview !== false,
    questions,
  };
};

export const getCoachReviewTriggerLabel = (value) =>
  COACH_REVIEW_TRIGGERS.find((item) => item.value === value)?.title || value || "—";

/** Exact labels from GymRite Review Due Logic. */
export const getCoachReviewStatusLabels = () => ({
  awaiting_member: "Awaiting Member Submission",
  review_due: "Review Due",
  overdue: "Review Overdue",
  completed: "Follow-up Completed",
});

export const getCategoriesForKind = (kind) =>
  TRACKER_CATEGORY_OPTIONS.filter((option) => option.kinds.includes(kind));

export const getAllPurposeOptions = () => TRACKER_CATEGORY_OPTIONS;

export const getCategoryMeta = (category) =>
  TRACKER_CATEGORY_OPTIONS.find((option) => option.value === category) || null;

export const getCategoryLabel = (category) =>
  getCategoryMeta(category)?.title || category || "—";

/** Standard one-off capture (lead, event, leaderboard) — no journey setup. */
export const isStandardTracker = (tracker) =>
  tracker?.kind === TRACKER_KINDS.STANDARD ||
  tracker?.type === TRACKER_KINDS.STANDARD ||
  tracker?.category === TRACKER_CATEGORIES.LEAD_CAPTURE ||
  tracker?.category === TRACKER_CATEGORIES.EVENT_REGISTRATION ||
  tracker?.category === TRACKER_CATEGORIES.LEADERBOARD;

export const isLeadCaptureTracker = (tracker) =>
  tracker?.category === TRACKER_CATEGORIES.LEAD_CAPTURE;

/** Journey coaches / check-in / review workflow — not used for lead capture. */
export const trackerUsesCoachWorkflow = (tracker) =>
  Boolean(tracker) && !isLeadCaptureTracker(tracker) && Boolean(tracker.createSetup);

export const getKindLabel = (kind) =>
  kind === TRACKER_KINDS.JOURNEY ? "Journey" : "Standard journey";

export const getOpenScheduleLabel = (value) =>
  OPEN_SCHEDULE_OPTIONS.find(
    (item) => item.value === normalizeOpenSchedule(value)
  )?.label ||
  value ||
  "—";

export const getJourneyStartLabel = (value) =>
  JOURNEY_START_OPTIONS.find(
    (item) => item.value === normalizeJourneyStartsFrom(value)
  )?.label ||
  value ||
  "—";

export const REVIEW_SCHEDULE_TYPES = {
  RELATIVE: "relative",
  CALENDAR: "calendar",
  CUSTOM: "custom",
};

/** GymRite Journey Review Scheduling — Relative / Calendar / Custom. */
export const REVIEW_SCHEDULE_TYPE_OPTIONS = [
  {
    value: REVIEW_SCHEDULE_TYPES.RELATIVE,
    title: "Relative Review Schedule",
    description:
      "Reviews occur relative to the person's sign-up / journey start — e.g. 7 Days, 30 Days, 90 Days, 6 Months, 12 Months.",
  },
  {
    value: REVIEW_SCHEDULE_TYPES.CALENDAR,
    title: "Calendar Review Schedule",
    description:
      "Reviews on fixed calendar dates — e.g. quarterly 1 January, 1 April, 1 July, 1 October. Mid-cycle joiners enter the next scheduled cycle.",
  },
  {
    value: REVIEW_SCHEDULE_TYPES.CUSTOM,
    title: "Custom Review Schedule",
    description:
      "Administrators build the cadence — e.g. Week 1 / 4 / 8 / 12 or Day 3 / 14 / 30 / 90.",
  },
];

/** Map older per-tracker automation event ids → GymRite event list. */
export const LEGACY_AUTOMATION_EVENT_IDS = {
  on_first_submission: "new_submission",
  on_follow_up_submission: "submission_updated",
  member_review_due: "review_due",
  member_review_overdue: "review_overdue",
  coach_review_due: "review_due",
  coach_review_overdue: "review_overdue",
  journey_completed: "milestone_achieved",
  unassigned_coach: "follow_up_outstanding",
};

export const normalizeAutomationEventId = (id) =>
  LEGACY_AUTOMATION_EVENT_IDS[id] || id;

export const normalizeAutomationEvents = (events) => {
  const defaults = createDefaultAutomation().events;
  const byId = new Map();
  (Array.isArray(events) ? events : []).forEach((item) => {
    if (!item?.id) return;
    const id = normalizeAutomationEventId(item.id);
    const prev = byId.get(id);
    byId.set(id, {
      id,
      enabled: Boolean(item.enabled) || Boolean(prev?.enabled),
    });
  });
  return defaults.map((def) => {
    const existing = byId.get(def.id);
    return existing ? { ...def, ...existing, id: def.id } : def;
  });
};

export const RELATIVE_OFFSET_UNITS = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
];

export const CUSTOM_SCHEDULE_UNITS = [
  { value: "days", label: "Days after join" },
  { value: "weeks", label: "Weeks after join" },
];

export const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const scheduleId = (prefix) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

export const createDefaultReviewSchedule = () => ({
  type: REVIEW_SCHEDULE_TYPES.RELATIVE,
  relativeOffsets: [],
  calendarDates: [],
  customUnit: "days",
  customPoints: [],
});

export const REVIEW_POINT_VIEW_THRESHOLD = 10;

export const REVIEW_POINT_UNITS = [
  { value: "days", label: "Day(s)" },
  { value: "weeks", label: "Week(s)" },
  { value: "months", label: "Month(s)" },
];

/** Schedule point unit that matches the check-in frequency on Basics. */
export const getSchedulePointUnit = (setup = {}) => {
  const mode = normalizeCheckInMode(setup?.checkInMode);
  if (mode === "weekly") return "weeks";
  if (mode === "monthly") return "months";
  return "days"; // daily, or custom "every N days"
};

export const formatReviewPointLabel = (point = {}) => {
  if (point.label && String(point.label).trim()) return String(point.label).trim();
  const amount = Number(point.amount);
  const unit = point.unit || "weeks";
  if (!Number.isFinite(amount) || amount <= 0) return "Review point";
  if (unit === "days") return amount === 1 ? "Day 1" : `Day ${amount}`;
  if (unit === "months")
    return amount === 1 ? "Month 1" : `Month ${amount}`;
  return amount === 1 ? "Week 1" : `Week ${amount}`;
};

/**
 * Journey submission / Extra-question points from How long (Basics).
 * Unlimited → none by default (user adds day/week/month points).
 * N weeks → Week 1..N; custom days → Day 1..N.
 */
export const getDurationBasedSchedulePoints = (setup = {}) => {
  const normalized = normalizeJourneyCoachReviewSetup(setup);

  if (isJourneyDurationUnlimited(normalized)) {
    return [];
  }

  if (normalized.durationWeeks === "custom") {
    const days = Math.max(
      1,
      Number(normalized.customDurationDays) || getMaxReviewDay(normalized) || 21
    );
    return Array.from({ length: days }, (_, index) => {
      const day = index + 1;
      return {
        key: `day_${day}`,
        label: `Day ${day}`,
        amount: day,
        unit: "days",
      };
    });
  }

  const weeks = Math.max(1, getMaxReviewWeek(normalized) || 12);
  return Array.from({ length: weeks }, (_, index) => {
    const week = index + 1;
    return {
      key: `week_${week}`,
      label: `Week ${week}`,
      amount: week,
      unit: "weeks",
    };
  });
};

export const createEmptyReviewPointSetting = (point = {}) => ({
  key: point.key || `point_${Date.now()}`,
  label: point.label || "Review point",
  amount: point.amount ?? null,
  unit: point.unit || "weeks",
  month: point.month ?? null,
  day: point.day ?? null,
  extraQuestions: [],
  excludedSharedQuestionIds: [],
});

const normalizeReviewPointExtras = (prev = {}) => ({
  extraQuestions: Array.isArray(prev.extraQuestions)
    ? prev.extraQuestions.map((q, index) => ({
        id:
          q.id ||
          `extra_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        label: q.label || "",
        fieldType: q.fieldType || "short_text",
        helpText: q.helpText || "",
        required: Boolean(q.required),
        options: Array.isArray(q.options) ? q.options : [],
        questionId: q.questionId || null,
        source: q.source || (q.questionId ? "library" : "tracker"),
        order: index,
      }))
    : [],
  excludedSharedQuestionIds: Array.isArray(prev.excludedSharedQuestionIds)
    ? prev.excludedSharedQuestionIds
    : [],
});

/** User-authored points when How long is unlimited / no end. */
export const normalizeCustomReviewPointSettings = (existing = []) => {
  const list = (Array.isArray(existing) ? existing : [])
    .filter((item) => {
      if (!item) return false;
      const key = String(item.key || "");
      // Keep user-added points; drop legacy auto Week 4/8/12 style keys.
      if (key.startsWith("custom_")) return true;
      if (/^(week|day)_\d+$/.test(key)) return false;
      return item.amount != null;
    })
    .map((item, index) => {
      const unit =
        item.unit === "days" || item.unit === "months" || item.unit === "weeks"
          ? item.unit
          : "weeks";
      const amount = Math.max(1, Number(item.amount) || 1);
      const base = createEmptyReviewPointSetting({
        key:
          item.key ||
          `custom_${unit}_${amount}_${index}_${Math.random()
            .toString(36)
            .slice(2, 6)}`,
        amount,
        unit,
        label: formatReviewPointLabel({ amount, unit, label: item.label }),
      });
      return {
        ...base,
        ...normalizeReviewPointExtras(item),
        key: base.key.startsWith("custom_")
          ? base.key
          : `custom_${unit}_${amount}_${index}`,
        amount,
        unit,
        label: formatReviewPointLabel({
          amount,
          unit,
          label: item.label || base.label,
        }),
      };
    });

  return list.sort((a, b) => {
    const unitRank = { days: 1, weeks: 7, months: 30 };
    const aDays = (Number(a.amount) || 0) * (unitRank[a.unit] || 7);
    const bDays = (Number(b.amount) || 0) * (unitRank[b.unit] || 7);
    return aDays - bDays;
  });
};

/** Merge duration points with saved extras/excludes (by key). */
export const syncReviewPointSettings = (setup = {}, existing = []) => {
  const normalized = normalizeJourneyCoachReviewSetup(setup);

  if (isJourneyDurationUnlimited(normalized)) {
    return normalizeCustomReviewPointSettings(existing);
  }

  const points = getDurationBasedSchedulePoints(normalized);
  const byKey = new Map(
    (Array.isArray(existing) ? existing : [])
      .filter((item) => item?.key)
      .map((item) => [item.key, item])
  );
  return points.map((point) => {
    const prev = byKey.get(point.key);
    if (!prev) return createEmptyReviewPointSetting(point);
    return {
      ...createEmptyReviewPointSetting(point),
      ...normalizeReviewPointExtras(prev),
      key: point.key,
      label: point.label,
      amount: point.amount ?? prev.amount ?? null,
      unit: point.unit || prev.unit || "weeks",
      month: point.month ?? prev.month ?? null,
      day: point.day ?? prev.day ?? null,
    };
  });
};

/** Build submissionSchedule from duration-based points. */
export const buildSubmissionScheduleFromReviewPoints = (points = []) => {
  const list = Array.isArray(points) ? points : [];
  const calendar = list.filter((p) => p.unit === "calendar");
  if (calendar.length) {
    return {
      ...createDefaultReviewSchedule(),
      type: REVIEW_SCHEDULE_TYPES.CALENDAR,
      relativeOffsets: [],
      calendarDates: calendar.map((p, index) => ({
        id: p.key || `cal_${index}`,
        label: p.label || `Review ${index + 1}`,
        month: Number(p.month) || 1,
        day: Number(p.day) || 1,
      })),
    };
  }
  return {
    ...createDefaultReviewSchedule(),
    type: REVIEW_SCHEDULE_TYPES.RELATIVE,
    calendarDates: [],
    relativeOffsets: list
      .filter(
        (p) =>
          p.unit === "weeks" || p.unit === "days" || p.unit === "months"
      )
      .map((p, index) => ({
        id: p.key || `rel_${index}`,
        label: p.label || `Checkpoint ${index + 1}`,
        amount: Number(p.amount) || 0,
        unit:
          p.unit === "weeks"
            ? "weeks"
            : p.unit === "months"
              ? "months"
              : "days",
      })),
  };
};

/** Migrate older repeat/checkpoint shape → Journey review schedule. */
export const normalizeReviewSchedule = (schedule) => {
  const defaults = createDefaultReviewSchedule();
  if (!schedule || typeof schedule !== "object") return defaults;

  if (
    schedule.type === REVIEW_SCHEDULE_TYPES.RELATIVE ||
    schedule.type === REVIEW_SCHEDULE_TYPES.CALENDAR ||
    schedule.type === REVIEW_SCHEDULE_TYPES.CUSTOM
  ) {
    return {
      ...defaults,
      ...schedule,
      relativeOffsets: Array.isArray(schedule.relativeOffsets)
        ? schedule.relativeOffsets
        : defaults.relativeOffsets,
      calendarDates: Array.isArray(schedule.calendarDates)
        ? schedule.calendarDates
        : defaults.calendarDates,
      customUnit: schedule.customUnit || "days",
      customPoints: Array.isArray(schedule.customPoints)
        ? schedule.customPoints
        : defaults.customPoints,
    };
  }

  // Legacy: custom_days_after_join checkpoints → relative
  if (
    schedule.repeatType === "custom_days_after_join" &&
    Array.isArray(schedule.checkpoints) &&
    schedule.checkpoints.length
  ) {
    return {
      ...defaults,
      type: REVIEW_SCHEDULE_TYPES.RELATIVE,
      relativeOffsets: schedule.checkpoints.map((item) => ({
        id: item.id || scheduleId("rel"),
        label: item.name || `${item.daysAfterJoin || 0} Day Review`,
        amount: Number(item.daysAfterJoin) || 0,
        unit: "days",
      })),
    };
  }

  return defaults;
};

export const getSubmissionScheduleLabel = (schedule) => {
  const normalized = normalizeReviewSchedule(schedule);
  const meta = REVIEW_SCHEDULE_TYPE_OPTIONS.find(
    (item) => item.value === normalized.type
  );
  return meta?.title || "—";
};

/** @deprecated use getSubmissionScheduleLabel */
export const getReviewScheduleLabel = getSubmissionScheduleLabel;

export const countSubmissionCheckpoints = (schedule) => {
  const normalized = normalizeReviewSchedule(schedule);
  if (normalized.type === REVIEW_SCHEDULE_TYPES.RELATIVE) {
    return (normalized.relativeOffsets || []).length;
  }
  if (normalized.type === REVIEW_SCHEDULE_TYPES.CALENDAR) {
    return (normalized.calendarDates || []).length;
  }
  return (normalized.customPoints || []).length;
};

const relativeAmountToDays = (amount, unit) => {
  const n = Number(amount) || 0;
  if (unit === "weeks") return n * 7;
  if (unit === "months") return n * 30;
  return n;
};

/** Sign-up form + one form per submission checkpoint (different forms in order). */
export const buildSeriesFormsFromSchedule = (schedule, existingForms = []) => {
  const normalized = normalizeReviewSchedule(schedule);
  const existing = Array.isArray(existingForms) ? existingForms : [];
  const first =
    existing[0] ||
    createEmptyForm({
      name: "Sign-up & Baseline",
      role: "first_submission",
    });

  let checkpoints = [];
  if (normalized.type === REVIEW_SCHEDULE_TYPES.RELATIVE) {
    checkpoints = (normalized.relativeOffsets || []).map((item) => ({
      label: item.label || `${item.amount} ${item.unit}`,
      unlockDays: relativeAmountToDays(item.amount, item.unit || "days"),
    }));
  } else if (normalized.type === REVIEW_SCHEDULE_TYPES.CALENDAR) {
    checkpoints = (normalized.calendarDates || []).map((item) => ({
      label: item.label || "Review",
      unlockType: "after_submit",
      unlockDays: 30,
    }));
  } else {
    const unit = normalized.customUnit || "days";
    checkpoints = (normalized.customPoints || []).map((item) => ({
      label: item.name || "Checkpoint",
      unlockDays:
        unit === "weeks"
          ? (Number(item.value) || 0) * 7
          : Number(item.value) || 0,
    }));
  }

  const followUps = checkpoints.map((cp, index) => {
    const prior = existing[index + 1] || {};
    const isLast = index === checkpoints.length - 1;
    return createEmptyForm({
      ...prior,
      name: cp.label || prior.name || `Form ${index + 2}`,
      role: isLast ? "final" : "follow_up",
      unlockType: cp.unlockType || "after_days",
      unlockDays: cp.unlockDays ?? prior.unlockDays ?? 30,
      excludedSharedQuestionIds: prior.excludedSharedQuestionIds || [],
      sharedOverrides: prior.sharedOverrides || {},
      questions: prior.questions || [],
    });
  });

  const form1 = {
    ...first,
    role: "first_submission",
    excludedSharedQuestionIds: first.excludedSharedQuestionIds || [],
    sharedOverrides: first.sharedOverrides || {},
  };

  const all = [form1, ...followUps];
  all.forEach((form, index) => {
    form.nextFormId = index < all.length - 1 ? all[index + 1].id : "end";
  });
  return all;
};

/** @deprecated kept for older imports — Journey uses submission schedule types */
export const SUBMISSION_REPEAT_OPTIONS = [
  { value: "does_not_repeat", label: "Does not repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly_monday", label: "Weekly on Monday" },
  { value: "every_weekday", label: "Every weekday" },
  { value: "monthly_first", label: "Monthly on the 1st" },
  { value: "custom_days_after_join", label: "Custom days after join…" },
];

export const QUESTION_PATTERN_OPTIONS = [
  {
    value: "one_form_once",
    title: "One form, used once",
    description:
      "Lead, event, challenge, leaderboard. The person answers once. Status and tags move them — not another form.",
  },
  {
    value: "same_questions_repeating",
    title: "Same questions, repeating",
    description:
      "Weekly nutrition, weight check-ins. The form stays the same. Add a second form only if a milestone needs extra questions.",
  },
  {
    value: "different_forms_in_order",
    title: "Different forms, in order",
    description:
      "Sign-up then 30 / 60 / 90 day. Each form is a different set of questions. Next one opens after submit, or after N days.",
  },
];

export const FORM_ROLE_OPTIONS = [
  { value: "first_submission", label: "First submission" },
  { value: "follow_up", label: "Follow-up" },
  { value: "final", label: "Final" },
  { value: "custom", label: "Custom" },
  { value: "daily_check_in", label: "Daily check-in" },
];

export const UNLOCK_OPTIONS = [
  { value: "after_submit", label: "Opens after they submit this" },
  { value: "after_days", label: "Opens after a number of days" },
];

export const DEFAULT_STATUS_COLORS = [
  "#0369a1",
  "#b45309",
  "#047857",
  "#475569",
  "#b91c1c",
];

export const createEmptyForm = (overrides = {}) => ({
  id: `form_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  name: "Sign-up & Baseline",
  role: "first_submission",
  shortDescription: "",
  active: true,
  nextFormId: "end",
  unlockType: "after_submit",
  // The Start / first_submission form is open from day 0.
  unlockDays:
    (overrides.role || "first_submission") === "first_submission" ? 0 : 30,
  questions: [],
  excludedSharedQuestionIds: [],
  sharedOverrides: {},
  ...overrides,
});

export const createEmptyStatus = (name, color, extras = {}) => ({
  id: `status_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  name,
  color,
  isFinish: false,
  ...extras,
});

export const FORM_QUESTION_SOURCES = {
  LIBRARY: "library",
  TRACKER: "tracker",
  SHARED: "shared",
  PERSONAL_INFO: "personal_info",
};

/** Mandatory Person fields — first submission only (GymRite spec). */
export const PERSONAL_INFO_LIBRARY_IDS = [
  "q_first_name",
  "q_last_name",
  "q_email",
  "q_phone",
];

export const isPersonalInfoLibraryId = (questionId) =>
  PERSONAL_INFO_LIBRARY_IDS.includes(questionId);

export const isFirstSubmissionForm = (form, allForms = []) => {
  if (!form) return false;
  if (form.role === "first_submission") return true;
  const forms = Array.isArray(allForms) ? allForms : [];
  if (!forms.length) return false;
  const designated = forms.find((f) => f.role === "first_submission");
  if (designated) return designated.id === form.id;
  return forms[0]?.id === form.id;
};

/** Instance of a question on a tracker form — may reference the library. */
export const createFormQuestionFromLibrary = (libraryQuestion, order = 0) => {
  const enriched = enrichQuestionFromLibrary(
    {
      id: `tq_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      source: FORM_QUESTION_SOURCES.LIBRARY,
      questionId: libraryQuestion.id,
      label: "",
      placeholder: "",
      helpText: "",
      required: false,
      searchable: true,
      filterable: true,
      measurable: false,
      order,
      mode: "",
      fieldType: "",
      measureType: "",
      measureSeriesKey: "",
      measureRole: "",
      units: [],
      defaultUnit: "",
      measureDirection: "",
      scaleMin: undefined,
      scaleMax: undefined,
      options: [],
    },
    libraryQuestion
  );
  return {
    ...enriched,
    source: FORM_QUESTION_SOURCES.LIBRARY,
    questionId: libraryQuestion.id,
    required: false,
    searchable: true,
    filterable: true,
    order,
  };
};

/** Tracker-only question — not linked to the global library. */
export const createTrackerSpecificFormQuestion = (data = {}, order = 0) => ({
  id: `tq_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  source: FORM_QUESTION_SOURCES.TRACKER,
  questionId: null,
  label: data.label || "",
  placeholder: data.placeholder || "",
  helpText: data.helpText || "",
  required: Boolean(data.required),
  searchable: data.searchable !== false,
  filterable: data.filterable !== false,
  measurable: Boolean(data.measurable),
  order,
  mode: data.measurable
    ? "measurable"
    : data.mode || "general",
  fieldType: data.fieldType || "short_text",
  measureType: data.measureType || "",
  measureSeriesKey: data.measureSeriesKey || "",
  measureRole: data.measureRole || "",
  units: data.units || [],
  defaultUnit: data.defaultUnit || "",
  measureDirection: data.measureDirection || "descending",
  scaleMin: data.scaleMin ?? 1,
  scaleMax: data.scaleMax ?? 10,
  options: data.options || [],
});

export const normalizeFormQuestion = (question, index = 0) => {
  if (!question) return null;
  const isPersonalInfo =
    question.source === FORM_QUESTION_SOURCES.PERSONAL_INFO ||
    isPersonalInfoLibraryId(question.questionId);
  const isShared =
    !isPersonalInfo && question.source === FORM_QUESTION_SOURCES.SHARED;
  const isLibrary =
    !isShared &&
    !isPersonalInfo &&
    Boolean(question.questionId) &&
    question.source !== FORM_QUESTION_SOURCES.TRACKER;
  const measurable =
    question.measurable === true ||
    question.mode === "measurable";

  return {
    id:
      question.id ||
      `tq_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    source: isPersonalInfo
      ? FORM_QUESTION_SOURCES.PERSONAL_INFO
      : isShared
        ? FORM_QUESTION_SOURCES.SHARED
        : isLibrary
          ? FORM_QUESTION_SOURCES.LIBRARY
          : FORM_QUESTION_SOURCES.TRACKER,
    questionId:
      isPersonalInfo || isLibrary || isShared
        ? question.questionId || null
        : null,
    sharedQuestionId: question.sharedQuestionId || (isShared ? question.id : null),
    label: question.label || "",
    placeholder: question.placeholder || "",
    helpText: question.helpText || "",
    required: Boolean(question.required),
    searchable: question.searchable !== false,
    filterable: question.filterable !== false,
    measurable,
    order: question.order ?? index,
    mode: measurable ? "measurable" : question.mode || "general",
    fieldType: question.fieldType || "short_text",
    measureType: question.measureType || "",
    measureSeriesKey: question.measureSeriesKey || "",
    measureRole: question.measureRole || "",
    units: question.units || [],
    defaultUnit: question.defaultUnit || "",
    measureDirection: question.measureDirection || "descending",
    scaleMin: question.scaleMin ?? 1,
    scaleMax: question.scaleMax ?? 10,
    options: question.options || [],
  };
};

const isMeasurableFormQuestion = (q) =>
  Boolean(
    q &&
      (q.measurable === true ||
        String(q.mode || "").toLowerCase() === "measurable" ||
        String(q.measureType || "").trim())
  );

/**
 * Personal (first submit) → general shared + schedule extras → measurable.
 * Matches public join / member submit order.
 */
export const getEffectiveFormQuestions = (
  form,
  sharedQuestions = [],
  personalInfoQuestions = [],
  allForms = []
) => {
  const excluded = new Set(form?.excludedSharedQuestionIds || []);
  const overrides = form?.sharedOverrides || {};

  const withLibrary = (q) => {
    if (!q?.questionId) return q;
    const libraryItem = getQuestionById(q.questionId);
    return enrichQuestionFromLibrary(q, libraryItem);
  };

  const personal = isFirstSubmissionForm(form, allForms)
    ? (personalInfoQuestions || [])
        .map((q) => {
          const override = overrides[q.id] || {};
          return normalizeFormQuestion(
            withLibrary({
              ...q,
              ...override,
              required: true,
              source: FORM_QUESTION_SOURCES.PERSONAL_INFO,
              sharedQuestionId: q.id,
            }),
            0
          );
        })
        .filter(Boolean)
    : [];

  const shared = (sharedQuestions || [])
    .filter(
      (q) =>
        q &&
        !excluded.has(q.id) &&
        !isPersonalInfoLibraryId(q.questionId)
    )
    .map((q) => {
      const override = overrides[q.id] || {};
      return normalizeFormQuestion(
        withLibrary({
          ...q,
          ...override,
          source: FORM_QUESTION_SOURCES.SHARED,
          sharedQuestionId: q.id,
        }),
        0
      );
    })
    .filter(Boolean);

  const specific = (form?.questions || [])
    .filter((q) => q && !isPersonalInfoLibraryId(q?.questionId))
    .map((q) => normalizeFormQuestion(withLibrary(q), 0))
    .filter(Boolean);

  const sharedGeneral = shared.filter((q) => !isMeasurableFormQuestion(q));
  const specificGeneral = specific.filter((q) => !isMeasurableFormQuestion(q));
  const sharedMeasurable = shared.filter((q) => isMeasurableFormQuestion(q));
  const specificMeasurable = specific.filter((q) =>
    isMeasurableFormQuestion(q)
  );

  return [
    ...personal,
    ...sharedGeneral,
    ...specificGeneral,
    ...sharedMeasurable,
    ...specificMeasurable,
  ].map((q, index) => ({ ...q, order: index }));
};

/** Keep forms in sync with "How questions work over time". */
export const getFormsForQuestionPattern = (
  pattern,
  existingForms = [],
  options = {}
) => {
  const { expandSeries = false } = options;
  const forms = Array.isArray(existingForms) ? [...existingForms] : [];

  if (
    pattern === "one_form_once" ||
    pattern === "same_questions_repeating"
  ) {
    const first =
      forms[0] ||
      createEmptyForm({
        name:
          pattern === "same_questions_repeating"
            ? "Check-in form"
            : "Main form",
        role: "first_submission",
      });
    return [
      {
        ...first,
        nextFormId: "end",
        unlockType: "after_submit",
        role: first.role || "first_submission",
        excludedSharedQuestionIds: first.excludedSharedQuestionIds || [],
        sharedOverrides: first.sharedOverrides || {},
        name:
          first.name ||
          (pattern === "same_questions_repeating"
            ? "Check-in form"
            : "Main form"),
      },
    ];
  }

  // Series: keep existing forms unless empty, or admin just switched pattern.
  if (forms.length >= 2 || (forms.length === 1 && !expandSeries)) {
    return forms.map((form) => ({
      ...form,
      excludedSharedQuestionIds: form.excludedSharedQuestionIds || [],
      sharedOverrides: form.sharedOverrides || {},
    }));
  }

  const form1 = {
    ...(forms[0] ||
      createEmptyForm({
        name: "Sign-up & Baseline",
        role: "first_submission",
      })),
    role: "first_submission",
    excludedSharedQuestionIds: forms[0]?.excludedSharedQuestionIds || [],
    sharedOverrides: forms[0]?.sharedOverrides || {},
  };
  const form2 = createEmptyForm({
    name: "30 Day Review",
    role: "follow_up",
    unlockType: "after_days",
    unlockDays: 30,
  });
  const form3 = createEmptyForm({
    name: "60 Day Review",
    role: "follow_up",
    unlockType: "after_days",
    unlockDays: 60,
  });
  const form4 = createEmptyForm({
    name: "90 Day Final Review",
    role: "final",
    unlockType: "after_days",
    unlockDays: 90,
  });
  form1.nextFormId = form2.id;
  form2.nextFormId = form3.id;
  form3.nextFormId = form4.id;
  form4.nextFormId = "end";
  return [form1, form2, form3, form4];
};

const PERSONAL_INFO_SEED = [
  {
    questionId: "q_first_name",
    label: "First Name",
    placeholder: "Enter first name",
    fieldType: "short_text",
    mode: "general",
  },
  {
    questionId: "q_last_name",
    label: "Last Name",
    placeholder: "Enter last name",
    fieldType: "short_text",
    mode: "general",
  },
  {
    questionId: "q_email",
    label: "Email Address",
    placeholder: "Enter email address",
    fieldType: "email",
    mode: "general",
  },
  {
    questionId: "q_phone",
    label: "Phone Number",
    placeholder: "Enter phone number",
    fieldType: "phone",
    mode: "general",
  },
];

/** Mandatory on every tracker's first submission form. */
export const buildDefaultPersonalInfoQuestions = () =>
  PERSONAL_INFO_SEED.map((item, index) =>
    normalizeFormQuestion(
      {
        id: `personal_${item.questionId}`,
        source: FORM_QUESTION_SOURCES.PERSONAL_INFO,
        questionId: item.questionId,
        label: item.label,
        placeholder: item.placeholder,
        fieldType: item.fieldType,
        mode: item.mode,
        required: true,
        searchable: true,
        filterable: true,
      },
      index
    )
  );

export const normalizePersonalInfoQuestions = (questions) => {
  const defaults = buildDefaultPersonalInfoQuestions();
  const byLibraryId = new Map(
    (questions || [])
      .filter((q) => q?.questionId)
      .map((q) => [q.questionId, q])
  );
  const core = defaults.map((def, index) => {
    const existing = byLibraryId.get(def.questionId);
    const placeholder =
      existing?.placeholder && String(existing.placeholder).trim()
        ? existing.placeholder
        : def.placeholder;
    return normalizeFormQuestion(
      {
        ...def,
        ...(existing || {}),
        id: existing?.id || def.id,
        questionId: def.questionId,
        placeholder,
        required: true,
        source: FORM_QUESTION_SOURCES.PERSONAL_INFO,
      },
      index
    );
  });

  // Optional extras (e.g. City) kept after the mandatory 4.
  const extras = (questions || [])
    .filter(
      (q) =>
        q &&
        !isPersonalInfoLibraryId(q.questionId) &&
        (q.source === FORM_QUESTION_SOURCES.PERSONAL_INFO ||
          String(q.id || "").startsWith("personal_"))
    )
    .map((q, index) =>
      normalizeFormQuestion(
        {
          ...q,
          source: FORM_QUESTION_SOURCES.PERSONAL_INFO,
          questionId: q.questionId || null,
          required: q.required !== false,
        },
        defaults.length + index
      )
    );

  return [...core, ...extras];
};

/** @deprecated personal info is not shared across all forms */
export const buildDefaultSharedQuestions = () => [];

export const createEmptyTracker = () => {
  const forms = getFormsForQuestionPattern("different_forms_in_order", []);

  return {
    id: `tracker_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    name: "",
    description: "",
    visitorMessage: "",
    kind: TRACKER_KINDS.JOURNEY,
    category: TRACKER_CATEGORIES.JOURNEY,
    type: TRACKER_KINDS.JOURNEY,
    submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
    openSchedule: "always_open",
    openDate: null,
    closeDate: null,
    closesAfterDays: "",
    journeyStartsFrom: "from_people_start",
    publishDate: getTodayDateString(),
    questionPattern: "different_forms_in_order",
    trackerAccess: TRACKER_ACCESS.PUBLIC,
    programmes: [],
    personalInfoQuestions: buildDefaultPersonalInfoQuestions(),
    sharedQuestions: [],
    forms,
    statuses: [
      createEmptyStatus("Enrolled", DEFAULT_STATUS_COLORS[0], {
        assignedOnSubmit: true,
      }),
      createEmptyStatus("In Progress", DEFAULT_STATUS_COLORS[1]),
      createEmptyStatus("Journey Completed", DEFAULT_STATUS_COLORS[2], {
        isFinish: true,
      }),
      createEmptyStatus("Dropped Out", DEFAULT_STATUS_COLORS[3], {
        isFinish: true,
      }),
    ],
    // Empty until admin picks from Trackers → Tags library (no fake defaults).
    tags: [],
    submissionSchedule: createDefaultReviewSchedule(),
    questions: [],
    fields: [],
    coaches: [],
    publicSlug: "",
    backgroundImage: "",
    settings: {
      requirePassword: false,
      password: "",
      submissionLimit: "",
      confirmationMessage:
        "Thanks — your submission is in. A coach will pick this up from here.",
      confirmationEmailTemplate: "",
      confirmationEmailEnabled: false,
      noMatchBehaviour: NO_MATCH_BEHAVIOURS.CREATE,
      acceptingSubmissions: true,
      /** Doc: every Tracker supports Export to Excel (filtered). */
      excelExportEnabled: true,
      /** Doc: record Submitted By — Member | Coach | Admin | System/Integration */
      recordSubmittedBy: true,
    },
    permissions: createDefaultPermissions(),
    gymTv: {
      enabled: false,
      headline: "",
      showTop: 10,
      eligibleStatusIds: [],
    },
    gymTvViews: createDefaultGymTvViews(),
    automation: createDefaultAutomation(),
    coachReview: createDefaultCoachReview(),
    status: TRACKER_STATUS.DRAFT,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

/** Standard lead-capture draft — personal info form only, no coaches/check-ins. */
export const createLeadCaptureTracker = () => {
  const forms = getFormsForQuestionPattern("one_form_once", [
    createEmptyForm({
      id: `form_lead_${Date.now()}`,
      name: "Lead form",
      role: "first_submission",
      shortDescription: "Basic personal details for new leads.",
      nextFormId: "end",
      questions: [],
    }),
  ]);

  const leadExtraIds = [
    "q_primary_goal",
    "q_current_fitness",
    "q_preferred_gym",
    "q_programme",
    "q_consultation_reason",
    "q_preferred_times",
    "q_medical_notes",
    "q_how_feeling",
  ];

  const sharedQuestions = leadExtraIds
    .map((id, index) => {
      const lib = getQuestionById(id);
      if (!lib) return null;
      return {
        ...createFormQuestionFromLibrary(lib, index),
        id: `lead_shared_${id}`,
        required: ["q_primary_goal", "q_consultation_reason"].includes(id),
      };
    })
    .filter(Boolean);

  return {
    ...createEmptyTracker(),
    kind: TRACKER_KINDS.STANDARD,
    category: TRACKER_CATEGORIES.LEAD_CAPTURE,
    type: TRACKER_KINDS.STANDARD,
    submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
    questionPattern: "one_form_once",
    forms,
    createSetup: null,
    coaches: [],
    sharedQuestions,
    programmes: [],
    coachReview: {
      ...createDefaultCoachReview(),
      enabled: false,
      questions: [],
    },
    statuses: [
      createEmptyStatus("New Lead", DEFAULT_STATUS_COLORS[0], {
        assignedOnSubmit: true,
      }),
      createEmptyStatus("Contacted", DEFAULT_STATUS_COLORS[1]),
      createEmptyStatus("Converted", DEFAULT_STATUS_COLORS[2], {
        isFinish: true,
      }),
      createEmptyStatus("Lost", DEFAULT_STATUS_COLORS[3], {
        isFinish: true,
      }),
    ],
    tags: [],
    visitorMessage:
      "Tell us a little about you, then answer a few questions so we can help.",
    settings: {
      ...createEmptyTracker().settings,
      confirmationMessage:
        "Thanks — we've got your details. Someone will be in touch soon.",
    },
  };
};

const schedulePointId = (prefix, key) => `${prefix}_${key}`;

/** Build relative week offsets within a journey length (e.g. 4 → 4, 8, 12). */
export const buildWeekReviewOffsets = (durationWeeks, everyWeeks) => {
  const every = Math.max(1, Number(everyWeeks) || 4);
  const duration =
    durationWeeks === "ongoing" ||
    durationWeeks == null ||
    durationWeeks === "custom"
      ? every * 3
      : Math.max(every, Number(durationWeeks) || every * 3);
  const offsets = [];
  for (let week = every; week <= duration; week += every) {
    offsets.push({
      id: schedulePointId("rel", `w${week}`),
      label: `Week ${week} Review`,
      amount: week,
      unit: "weeks",
    });
  }
  return offsets.length
    ? offsets
    : [
        {
          id: schedulePointId("rel", `w${every}`),
          label: `Week ${every} Review`,
          amount: every,
          unit: "weeks",
        },
      ];
};

/** Build relative day offsets for custom-length journeys (e.g. 21 days). */
export const buildDayReviewOffsets = (durationDays, everyDays) => {
  const every = Math.max(1, Number(everyDays) || 7);
  const duration = Math.max(every, Number(durationDays) || every);
  const offsets = [];
  for (let day = every; day <= duration; day += every) {
    offsets.push({
      id: schedulePointId("rel", `d${day}`),
      label: `Day ${day} Review`,
      amount: day,
      unit: "days",
    });
  }
  return offsets.length
    ? offsets
    : [
        {
          id: schedulePointId("rel", `d${duration}`),
          label: `Day ${duration} Review`,
          amount: duration,
          unit: "days",
        },
      ];
};

export const describeWeekReviewPreview = (
  durationWeeks,
  everyWeeks,
  customDurationDays
) => {
  if (durationWeeks === "custom") {
    const everyDays = Math.max(1, (Number(everyWeeks) || 4) * 7);
    const offsets = buildDayReviewOffsets(customDurationDays, everyDays);
    return `→ days ${offsets.map((o) => o.amount).join(", ")}`;
  }
  const offsets = buildWeekReviewOffsets(durationWeeks, everyWeeks);
  return `→ weeks ${offsets.map((o) => o.amount).join(", ")}`;
};

const shortMonthLabel = (month) =>
  ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][
    Number(month) || 0
  ] || "";

/** Help text under Scheduled reviews. */
export const describeScheduledReviewsHelp = (setup = {}) => {
  const scheduled = normalizeScheduledReviews(setup);
  if (scheduled === "none") {
    return "No scheduled reviews — the coach replies to each check-in instead (below).";
  }
  if (scheduled === "fixed_dates") {
    return "The same dates for everyone, every year — a member joins the next date whatever their start (quarterly reviews).";
  }
  if (isJourneyDurationUnlimited(setup)) {
    return "Counted from each person's own start — journey has no end, so set any review weeks or days you need.";
  }
  if (setup.durationWeeks === "custom") {
    const days = getMaxReviewDay(setup);
    return `Counted from each person's own start, within the ${days}-day journey. Reviews cannot be past day ${days}.`;
  }
  const weeks = getMaxReviewWeek(setup);
  return `Counted from each person's own start, within the ${weeks}-week journey. Reviews cannot be past week ${weeks}.`;
};

/** Summary line under Reviews controls. */
export const describeCoachReviewsSummary = (setup = {}) => {
  const normalized = normalizeJourneyCoachReviewSetup(setup);
  const parts = [];
  if (normalized.scheduledReviews === "by_week") {
    parts.push(`week ${normalized.coachReviewWeeks.join(", ")}`);
  } else if (normalized.scheduledReviews === "by_day") {
    parts.push(`day ${normalized.coachReviewDays.join(", ")}`);
  } else if (normalized.scheduledReviews === "fixed_dates") {
    const dates = normalized.coachReviewFixedDates
      .map((d) => `${d.day} ${shortMonthLabel(d.month)}`)
      .join(", ");
    if (dates) parts.push(dates);
  }
  if (normalized.alsoReviewEveryCheckin) {
    const within =
      REPLY_WITHIN_DAYS_OPTIONS.find(
        (item) => item.value === normalized.replyWithinDays
      )?.label || `${normalized.replyWithinDays} days`;
    parts.push(
      normalized.replyWithinDays === 0
        ? "each check-in the same day"
        : `each check-in within ${within.replace(/^the /, "")}`
    );
  }
  if (!parts.length) return "No reviews configured.";
  return `Reviews: ${parts.join(" · ")}. The coach is reminded by the Review reminder.`;
};

/** Build submissionSchedule + coachReview trigger from Reviews controls. */
export const buildCoachReviewScheduleFromSetup = (setup = {}) => {
  const normalized = normalizeJourneyCoachReviewSetup(setup);
  const also = normalized.alsoReviewEveryCheckin;
  const coachReviewPatch = {
    trigger: also ? "after_each_submission" : "after_follow_ups",
    dueAfterDays: also ? normalized.replyWithinDays : 0,
  };

  let submissionSchedule = createDefaultReviewSchedule();
  let questionPattern = "different_forms_in_order";

  if (normalized.scheduledReviews === "fixed_dates") {
    submissionSchedule = {
      ...createDefaultReviewSchedule(),
      type: REVIEW_SCHEDULE_TYPES.CALENDAR,
      relativeOffsets: [],
      calendarDates: normalizeCoachReviewFixedDates(
        normalized.coachReviewFixedDates
      ),
    };
  } else if (normalized.scheduledReviews === "by_week") {
    submissionSchedule = {
      ...createDefaultReviewSchedule(),
      type: REVIEW_SCHEDULE_TYPES.RELATIVE,
      relativeOffsets: normalized.coachReviewWeeks.map((week) => ({
        id: schedulePointId("rel", `w${week}`),
        label: `Week ${week} Review`,
        amount: week,
        unit: "weeks",
      })),
      calendarDates: [],
    };
  } else if (normalized.scheduledReviews === "by_day") {
    submissionSchedule = {
      ...createDefaultReviewSchedule(),
      type: REVIEW_SCHEDULE_TYPES.RELATIVE,
      relativeOffsets: normalized.coachReviewDays.map((day) => ({
        id: schedulePointId("rel", `d${day}`),
        label: `Day ${day} Review`,
        amount: day,
        unit: "days",
      })),
      calendarDates: [],
    };
  } else if (also) {
    const days = getCheckInIntervalDays(normalized);
    const mode = normalizeCheckInMode(normalized.checkInMode);
    const checkInLabel =
      mode === "weekly"
        ? "Weekly check-in"
        : mode === "daily"
          ? "Daily check-in"
          : mode === "monthly"
            ? "Monthly check-in"
            : `Every ${days} days`;
    submissionSchedule = {
      ...createDefaultReviewSchedule(),
      type: REVIEW_SCHEDULE_TYPES.RELATIVE,
      relativeOffsets: [
        {
          id: schedulePointId("rel", "checkin"),
          label: checkInLabel,
          amount: days,
          unit: "days",
        },
      ],
      calendarDates: [],
    };
    questionPattern = "same_questions_repeating";
  }

  return {
    setup: normalized,
    submissionSchedule,
    coachReviewPatch,
    questionPattern,
  };
};

/**
 * Shape a new Journey tracker from the short create form
 * (name / programme / length / check-in / coach review).
 */
export const applyJourneyCreateSetup = (
  tracker,
  setup = {},
  programmes = []
) => {
  const nextSetup = normalizeJourneyCoachReviewSetup({
    ...createDefaultJourneyCreateSetup(),
    ...setup,
    checkInMode: normalizeCheckInMode(setup?.checkInMode),
    checkInMonthDay: normalizeCheckInMonthDay(setup?.checkInMonthDay),
    checkInWeekdays: Array.isArray(setup?.checkInWeekdays)
      ? setup.checkInWeekdays
      : createDefaultJourneyCreateSetup().checkInWeekdays,
  });
  const base = { ...createEmptyTracker(), ...tracker };

  const built = buildCoachReviewScheduleFromSetup(nextSetup);
  let coachReview = {
    ...createDefaultCoachReview(),
    ...built.coachReviewPatch,
  };
  let questionPattern = built.questionPattern;
  let submissionSchedule = built.submissionSchedule;

  const stamp = Date.now();
  // No seeded form-specific questions: the builder doesn't show them, so they
  // would appear on the join page invisibly and duplicate configured questions.
  const startQuestions = [];
  const checkInQuestions = [];

  const forms =
    questionPattern === "same_questions_repeating"
      ? [
          createEmptyForm({
            id: `form_checkin_${stamp}`,
            name: "Check-in form",
            role: "first_submission",
            shortDescription: "Same questions each check-in.",
            nextFormId: "end",
            unlockType: "after_submit",
            questions: checkInQuestions,
          }),
        ]
      : [
          createEmptyForm({
            id: `form_start_${stamp}`,
            name: "Start form",
            role: "first_submission",
            shortDescription: "Baseline when they join the journey.",
            nextFormId: `form_checkin_${stamp}`,
            unlockType: "after_submit",
            questions: startQuestions,
          }),
          createEmptyForm({
            id: `form_checkin_${stamp}`,
            name: "Check-in",
            role: "follow_up",
            shortDescription: "Member check-in questions.",
            nextFormId: "end",
            unlockType: "after_days",
            unlockDays: getCheckInIntervalDays(nextSetup),
            questions: checkInQuestions,
          }),
        ];

  return withProgrammePublicRules({
    ...base,
    kind: TRACKER_KINDS.JOURNEY,
    category: TRACKER_CATEGORIES.JOURNEY,
    type: TRACKER_KINDS.JOURNEY,
    submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
    journeyStartsFrom: normalizeJourneyStartsFrom(
      nextSetup.journeyStartsFrom || "from_people_start"
    ),
    publishDate: nextSetup.publishDate || getTodayDateString(),
    questionPattern,
    trackerAccess:
      Array.isArray(programmes) && programmes.length
        ? TRACKER_ACCESS.GYMRITE_MEMBERS
        : normalizeTrackerAccess(base.trackerAccess, { programmes }),
    programmes: Array.isArray(programmes) ? programmes : [],
    forms,
    submissionSchedule,
    coachReview,
    createSetup: nextSetup,
    visitorMessage:
      base.visitorMessage ||
      "Welcome — answer a few questions to start your journey.",
  });
};

/**
 * Update createSetup on an existing builder tracker.
 * Rebuilds schedule / coach-review trigger; keeps forms and authored fields.
 */
export const patchJourneyCreateSetup = (tracker, setupPatch = {}) => {
  if (!tracker) return tracker;
  const nextSetup = normalizeJourneyCoachReviewSetup({
    ...createDefaultJourneyCreateSetup(),
    ...(tracker.createSetup || {}),
    ...setupPatch,
    checkInMode: normalizeCheckInMode(
      setupPatch.checkInMode ?? tracker.createSetup?.checkInMode
    ),
  });
  const applied = applyJourneyCreateSetup(
    tracker,
    nextSetup,
    tracker.programmes || []
  );
  const preservedCoachReview = normalizeCoachReview(tracker.coachReview);
  const built = buildCoachReviewScheduleFromSetup(nextSetup);
  const wasUnlimited = isJourneyDurationUnlimited(tracker.createSetup || {});
  const nowUnlimited = isJourneyDurationUnlimited(nextSetup);
  const reviewPointSettings = syncReviewPointSettings(
    nextSetup,
    nowUnlimited && !wasUnlimited ? [] : tracker.reviewPointSettings
  );
  return {
    ...applied,
    id: tracker.id,
    name: tracker.name,
    description: tracker.description,
    visitorMessage: tracker.visitorMessage,
    publicSlug: tracker.publicSlug,
    status: tracker.status,
    coaches: tracker.coaches,
    statuses: tracker.statuses,
    tags: tracker.tags,
    settings: tracker.settings,
    permissions: tracker.permissions,
    automation: tracker.automation,
    gymTv: tracker.gymTv,
    gymTvViews: tracker.gymTvViews,
    personalInfoQuestions: tracker.personalInfoQuestions,
    sharedQuestions: tracker.sharedQuestions,
    forms: tracker.forms,
    questionPattern: tracker.questionPattern,
    reviewPointSettings,
    submissionSchedule:
      buildSubmissionScheduleFromReviewPoints(reviewPointSettings),
    createSetup: nextSetup,
    // Keep authored coach questions/settings; trigger + due follow Reviews controls.
    coachReview: {
      ...preservedCoachReview,
      ...built.coachReviewPatch,
    },
    openSchedule: normalizeOpenSchedule(tracker.openSchedule),
    openDate: tracker.openDate,
    closeDate: tracker.closeDate,
    closesAfterDays: tracker.closesAfterDays,
    createdAt: tracker.createdAt,
    updatedAt: tracker.updatedAt,
  };
};

const slugify = (value, fallback = "tracker") =>
  String(value || fallback)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || fallback;

/** Fill Journey builder fields for trackers created before step-2 UI. */
export const ensureTrackerShape = (tracker) => {
  if (!tracker) return null;

  const defaults = createEmptyTracker();
  const questionPattern =
    tracker.questionPattern || "different_forms_in_order";
  const mappedForms =
    Array.isArray(tracker.forms) && tracker.forms.length
      ? tracker.forms.map((form) => ({
          ...form,
          excludedSharedQuestionIds: Array.isArray(
            form.excludedSharedQuestionIds
          )
            ? form.excludedSharedQuestionIds
            : [],
          sharedOverrides:
            form.sharedOverrides &&
            typeof form.sharedOverrides === "object" &&
            !Array.isArray(form.sharedOverrides)
              ? form.sharedOverrides
              : {},
          questions: Array.isArray(form.questions)
            ? form.questions
                .map((q, index) => normalizeFormQuestion(q, index))
                .filter((q) => q && !isPersonalInfoLibraryId(q.questionId))
            : [],
        }))
      : [];

  const legacyShared = Array.isArray(tracker.sharedQuestions)
    ? tracker.sharedQuestions
    : [];
  const personalFromLegacy = legacyShared.filter((q) =>
    isPersonalInfoLibraryId(q?.questionId)
  );
  const sharedFromLegacy = legacyShared.filter(
    (q) => !isPersonalInfoLibraryId(q?.questionId)
  );

  const personalInfoQuestions = normalizePersonalInfoQuestions(
    Array.isArray(tracker.personalInfoQuestions) &&
      tracker.personalInfoQuestions.length
      ? tracker.personalInfoQuestions
      : personalFromLegacy.length
        ? personalFromLegacy
        : buildDefaultPersonalInfoQuestions()
  );

  const sharedSource =
    sharedFromLegacy.length > 0
      ? sharedFromLegacy
      : (tracker.sharedQuestions || []).filter(
          (q) => q && !isPersonalInfoLibraryId(q.questionId)
        );

  const sharedQuestions = sharedSource
    .map((q, index) => normalizeFormQuestion(q, index))
    .filter(Boolean);

  const trackerAccess = normalizeTrackerAccess(tracker.trackerAccess, {
    programmes: Array.isArray(tracker.programmes) ? tracker.programmes : [],
  });
  const programmes =
    trackerAccess === TRACKER_ACCESS.PUBLIC
      ? []
      : Array.isArray(tracker.programmes)
        ? tracker.programmes
            .map((item) => {
              if (!item) return null;
              if (typeof item === "string") {
                return { id: item, title: item };
              }
              return {
                id: item.id || item._id,
                title: item.title || item.name || "",
                program_length_type: item.program_length_type || "",
                no_of_weeks: item.no_of_weeks,
              };
            })
            .filter((item) => item?.id)
        : [];

  const next = {
    ...defaults,
    ...tracker,
    kind: isStandardTracker(tracker)
      ? TRACKER_KINDS.STANDARD
      : TRACKER_KINDS.JOURNEY,
    category:
      tracker.category ||
      (isStandardTracker(tracker)
        ? TRACKER_CATEGORIES.LEAD_CAPTURE
        : TRACKER_CATEGORIES.JOURNEY),
    type: isStandardTracker(tracker)
      ? TRACKER_KINDS.STANDARD
      : TRACKER_KINDS.JOURNEY,
    visitorMessage: tracker.visitorMessage || "",
    journeyStartsFrom: normalizeJourneyStartsFrom(
      tracker.journeyStartsFrom || defaults.journeyStartsFrom
    ),
    publishDate:
      tracker.publishDate ||
      tracker.createSetup?.publishDate ||
      getTodayDateString(),
    questionPattern,
    trackerAccess,
    programmes,
    submissionMode: SUBMISSION_MODES.UPDATE_EXISTING,
    forms: getFormsForQuestionPattern(questionPattern, mappedForms),
    personalInfoQuestions,
    sharedQuestions,
    createSetup: tracker.createSetup
      ? normalizeJourneyCoachReviewSetup({
          ...createDefaultJourneyCreateSetup(),
          ...tracker.createSetup,
          journeyStartsFrom: normalizeJourneyStartsFrom(
            tracker.createSetup.journeyStartsFrom ||
              tracker.journeyStartsFrom
          ),
          publishDate:
            tracker.createSetup.publishDate ||
            tracker.publishDate ||
            getTodayDateString(),
          checkInMode: normalizeCheckInMode(tracker.createSetup.checkInMode),
          checkInMonthDay: normalizeCheckInMonthDay(
            tracker.createSetup.checkInMonthDay
          ),
        })
      : null,
    statuses:
      Array.isArray(tracker.statuses) && tracker.statuses.length
        ? tracker.statuses
        : defaults.statuses,
    tags: Array.isArray(tracker.tags) ? tracker.tags : defaults.tags,
    submissionSchedule: normalizeReviewSchedule(tracker.submissionSchedule),
    reviewPointSettings: syncReviewPointSettings(
      tracker.createSetup || createDefaultJourneyCreateSetup(),
      tracker.reviewPointSettings
    ),
    coaches: (() => {
      const normalizedPermissions = normalizePermissions(tracker.permissions);
      return Array.isArray(tracker.coaches)
        ? tracker.coaches
            .map((item) =>
              normalizeCoachWithPermissions(item, normalizedPermissions)
            )
            .filter((item) => item?.id)
        : [];
    })(),
    questions: Array.isArray(tracker.questions) ? tracker.questions : [],
    publicSlug:
      tracker.publicSlug || slugify(tracker.name, tracker.id || "tracker"),
    backgroundImage: tracker.backgroundImage || "",
    settings: {
      ...defaults.settings,
      ...(tracker.settings || {}),
      noMatchBehaviour: NO_MATCH_BEHAVIOURS.CREATE,
      acceptingSubmissions:
        tracker.settings?.acceptingSubmissions !== false,
      excelExportEnabled: tracker.settings?.excelExportEnabled !== false,
      recordSubmittedBy: tracker.settings?.recordSubmittedBy !== false,
    },
    permissions: normalizePermissions(tracker.permissions),
    gymTv: {
      ...defaults.gymTv,
      ...(tracker.gymTv || {}),
      eligibleStatusIds: Array.isArray(tracker.gymTv?.eligibleStatusIds)
        ? tracker.gymTv.eligibleStatusIds
        : defaults.gymTv.eligibleStatusIds,
    },
    gymTvViews:
      Array.isArray(tracker.gymTvViews) && tracker.gymTvViews.length
        ? tracker.gymTvViews
        : defaults.gymTvViews,
    automation: {
      ...defaults.automation,
      ...(tracker.automation || {}),
      automationId: tracker.automation?.automationId || "",
      workflowIds: Array.isArray(tracker.automation?.workflowIds)
        ? tracker.automation.workflowIds
        : defaults.automation.workflowIds,
      events: normalizeAutomationEvents(tracker.automation?.events),
    },
    coachReview: normalizeCoachReview(tracker.coachReview),
  };

  return withProgrammePublicRules(next);
};

export const normalizeTracker = (tracker) => {
  if (!tracker) return null;
  const id = tracker._id || tracker.id;
  const shaped = ensureTrackerShape({
    ...tracker,
    id,
    _id: id,
  });
  return {
    ...shaped,
    id,
    _id: id,
  };
};

/** Strip client-only keys so Joi create/update validation succeeds. */
export const toTrackerApiPayload = (tracker = {}) => {
  const source = tracker || {};
  const statusRaw = String(source.status || "draft").toLowerCase();
  const status =
    statusRaw === "published"
      ? "live"
      : ["draft", "live", "archived"].includes(statusRaw)
        ? statusRaw
        : "draft";

  return {
    name: String(source.name || "").trim(),
    description: source.description || "",
    visitorMessage: source.visitorMessage || "",
    kind: source.kind === "standard" ? "standard" : "journey",
    category: source.category || "journey",
    type: source.type || source.kind || "journey",
    status,
    publicSlug: source.publicSlug || "",
    backgroundImage: source.backgroundImage || "",
    trackerAccess: source.trackerAccess || "public",
    programmes: Array.isArray(source.programmes) ? source.programmes : [],
    journeyStartsFrom: source.journeyStartsFrom || "from_people_start",
    publishDate: source.publishDate || "",
    questionPattern: source.questionPattern || "different_forms_in_order",
    submissionMode: source.submissionMode || "update_existing",
    forms: Array.isArray(source.forms) ? source.forms : [],
    personalInfoQuestions: Array.isArray(source.personalInfoQuestions)
      ? source.personalInfoQuestions
      : [],
    sharedQuestions: Array.isArray(source.sharedQuestions)
      ? source.sharedQuestions
      : [],
    createSetup: source.createSetup || null,
    statuses: Array.isArray(source.statuses) ? source.statuses : [],
    tags: Array.isArray(source.tags) ? source.tags : [],
    submissionSchedule: source.submissionSchedule || null,
    reviewPointSettings: Array.isArray(source.reviewPointSettings)
      ? source.reviewPointSettings
      : [],
    coaches: Array.isArray(source.coaches) ? source.coaches : [],
    settings:
      source.settings && typeof source.settings === "object"
        ? source.settings
        : {},
    permissions:
      source.permissions && typeof source.permissions === "object"
        ? source.permissions
        : {},
    gymTv:
      source.gymTv && typeof source.gymTv === "object" ? source.gymTv : {},
    gymTvViews: Array.isArray(source.gymTvViews) ? source.gymTvViews : [],
    automation:
      source.automation && typeof source.automation === "object"
        ? source.automation
        : {},
    coachReview:
      source.coachReview && typeof source.coachReview === "object"
        ? source.coachReview
        : {},
  };
};

export const exportTrackerStructure = (tracker) => {
  const shaped = ensureTrackerShape(tracker);
  if (!shaped) return null;
  return {
    exportedAt: new Date().toISOString(),
    kind: shaped.kind,
    name: shaped.name,
    description: shaped.description,
    visitorMessage: shaped.visitorMessage,
    questionPattern: shaped.questionPattern,
    personalInfoQuestions: shaped.personalInfoQuestions,
    sharedQuestions: shaped.sharedQuestions,
    forms: shaped.forms,
    statuses: shaped.statuses,
    tags: shaped.tags,
    submissionSchedule: shaped.submissionSchedule,
    programmes: shaped.programmes,
    settings: shaped.settings,
    permissions: shaped.permissions,
    automation: shaped.automation,
    coachReview: shaped.coachReview,
    gymTv: shaped.gymTv,
    gymTvViews: shaped.gymTvViews,
  };
};
